import { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { Modal, Box, Button, IconButton, Tooltip, TextField, Autocomplete, Popper, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import PythonService from '../../../Services/pythonScripts.service';
import EnumsService from '../../../Services/enums.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import TocIcon from '@mui/icons-material/Toc';
import { FormControlLabel, Checkbox, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { styled, darken, lighten } from "@mui/material/styles";
import * as XLSX from 'xlsx-js-style';
import JSON5 from 'json5';
import { useTranslation } from 'react-i18next';
import Constants from '../../../assests/Constants/constants';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext';
import { toast } from 'react-toastify';
import ChatIcon from '@mui/icons-material/Chat';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CompareIcon from '@mui/icons-material/Compare';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TrialBalanceMonthlyReportModal from './TrialBalanceMonthlyReportModal';
import ReportCurrencySelector from '../ReportCurrencySelector/ReportCurrencySelector';
import ReportLanguageSelector from '../ReportLanguageSelector/ReportLanguageSelector';
import { useReportTranslation } from '../../../hooks/useReportTranslation';
import PeriodSelectionModal from '../ProfitAndLossReportHtml/PeriodSelectionModal';
import { getCaseNumberLabel } from '../../../Utils/ReportUtils';
import { buildReportWorkbook, applyWorkbookRtl, AMOUNT_NUMBER_FORMAT } from '../../../Utils/ExcelReport';
import { usePeriodComparison } from '../../../hooks/usePeriodComparison';
import { PermissionsContext } from '../../../Contexts/Permissions';
import SignatureModal from '../../Widgets/SignatureModal/SignatureModal';
import Swal from 'sweetalert2';
import ProjectsService from '../../../Services/projects.service';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    height: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '10px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};
const transactionsPerPage = 13;

// Sleek styled button component
const SleekButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
    border: 'none',
    color: '#ffffff',
    minWidth: 'auto',
    height: '36px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    '&:active': {
        transform: 'translateY(0px)',
        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
    },
    '& .MuiButton-startIcon': {
        marginRight: '6px',
        marginLeft: '0px',
        '& svg': {
            fontSize: '16px',
        }
    }
}));

// Sleek icon button component
const SleekIconButton = styled(IconButton)(({ theme }) => ({
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
    color: '#ffffff',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
    },
    '&:active': {
        transform: 'translateY(0px)',
        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
    },
    '& svg': {
        fontSize: '18px',
    }
}));

// Sleek toggle button group
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    marginBottom: "10px",
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    marginTop: '10px',
    "& .MuiToggleButton-root": {
        width: "120px",
        height: "32px",
        fontSize: "13px",
        fontWeight: "600",
        textTransform: 'none',
        backgroundColor: "#ffffff",
        border: 'none',
        borderRadius: '0',
        padding: '4px 8px',
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
            backgroundColor: "#f8f9ff",
            transform: "translateY(-1px)",
        },
        "&.Mui-selected": {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: "#ffffff",
            "&:hover": {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                transform: "translateY(-1px)",
            },
        },
        "&:first-of-type": {
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
        },
        "&:last-of-type": {
            borderTopRightRadius: '8px',
            borderBottomRightRadius: '8px',
        },
        "& .MuiButton-startIcon": {
            marginRight: '4px',
            marginLeft: '0px',
            '& svg': {
                fontSize: '14px',
            }
        }
    },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 'var(--Spacing-md, 8px)',
        background: 'var(--white, #FFF)',
        boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
    },
    '& .MuiAutocomplete-option': {
        borderRadius: 'var(--Spacing-sm, 4px)',
        '&[aria-selected="true"], &.Mui-focused, &:hover': {
            background: 'var(--orange, #F9AA2A) !important',
        },
    },
}));

const escapeHtmlAttr = (s) =>
    String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const compareProjectCodes = (a, b) =>
    String(a?.code ?? '').localeCompare(String(b?.code ?? ''), undefined, { numeric: true });

class PalCodeAccountGroupDetails {
    constructor(name, count = 0, appeared = 0, totalInitDebit = 0, totalInitCredit = 0, totalDebit = 0, totalCredit = 0, totalBalance = 0) {
        this.name = name;
        this.count = count;
        this.appeared = appeared;
        this.totalInitDebit = totalInitDebit;
        this.totalInitCredit = totalInitCredit;
        this.totalDebit = totalDebit;
        this.totalCredit = totalCredit;
        this.totalBalance = totalBalance;
    }

    // Method to retrieve the object as JSON
    toJSON() {
        return {
            name: this.name,
            totalInitDebit: this.totalInitDebit,
            totalInitCredit: this.totalInitCredit,
            totalDebit: this.totalDebit,
            totalCredit: this.totalCredit,
            totalBalance: this.totalBalance
        };
    }
}

const TrialBalanceReportHtml = ({
    open,
    handleClose,
    data,
    handleSelect,
    token,
    client,
    showAccountCardsWithZeros,
    setShowAccountCardsWithZeros,
    palCodesData,
    palDates,
    isMonthly,
    onTrialBalanceSegmentationFilterChange,
    selectedCurrency = null,
    availableCurrencies = [],
    onCurrencyChange,
}) => {
    const noopSegmentationChange = useCallback(() => {}, []);
    const notifySegmentationFilterChange = onTrialBalanceSegmentationFilterChange ?? noopSegmentationChange;

    // Chat functionality
    const { handleSendMessageToChat, selectUserAndOpenChat } = useHamburgerMenu();
    const { userTypeContext, permissionsState } = useContext(PermissionsContext);
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    const [detailedTransactions, setDetailedTransactions] = useState(true);
    // When false, the opening-balance columns (י. פ. חובה / י. פ. זכות) are hidden
    // and their amounts are folded into the חובה / זכות columns.
    const [showOpeningColumns, setShowOpeningColumns] = useState(true);
    // When true, rows whose displayed amounts are all zero are hidden.
    const [hideZeroRows, setHideZeroRows] = useState(false);
    const [trialBalanceType, setTrialBalanceType] = useState("PalCodes");
    const [showMonthlyReportModal, setShowMonthlyReportModal] = useState(false);

    const [reportHtmlContent, setReportHtmlContent] = useState('');
    const {
        reportLanguage,
        setReportLanguage,
        isTranslating,
        displayHtml,
        previewDir,
        translateHtml,
        translateWb,
    } = useReportTranslation(reportHtmlContent, token);
    const [clientData, setClientData] = useState('');
    const [viewAccountCard, setViewAccountCard] = useState(false);
    const [viewPalcodes, setViewPalcodes] = useState(true);

    const [segmentationProjects, setSegmentationProjects] = useState([]);
    const [loadingSegmentations, setLoadingSegmentations] = useState(false);
    const [selectedFromSegmentation, setSelectedFromSegmentation] = useState(null);
    const [selectedToSegmentation, setSelectedToSegmentation] = useState(null);

    const [showWithZeros, setShowWithZeros] = useState(showAccountCardsWithZeros);
    const [showAccountCodeId, setShowAccountCodeId] = useState(true);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [profitAndLossNames, setProfitAndLossNames] = useState([]);
    const [accountingGroups, setAccountingGroups] = useState(null);

    const projectsTrialBalanceApiFilter = useMemo(() => {
        if (trialBalanceType !== 'Projects') return null;
        const payload = { trialBalanceType: 'Projects' };
        if (selectedFromSegmentation?.id) {
            payload.fromSegmentationId = selectedFromSegmentation.id;
        }
        if (selectedToSegmentation?.id) {
            payload.toSegmentationId = selectedToSegmentation.id;
        }
        return payload;
    }, [trialBalanceType, selectedFromSegmentation, selectedToSegmentation]);

    const {
        showPeriodComparison,
        periodComparisonData,
        showPeriodSelectionModal,
        selectedPeriod1,
        selectedPeriod2,
        isLoadingComparison,
        setSelectedPeriod1,
        setSelectedPeriod2,
        handleOpenPeriodSelection,
        handleClosePeriodSelection,
        handleFetchComparisonData,
        handleCloseComparison
    } = usePeriodComparison(client, token, false, projectsTrialBalanceApiFilter);

    // Declare the total accumulators at the component level
    let totalInitDebitAccountReport = 0;
    let totalInitCreditAccountReport = 0;
    let totalDebitAccountReport = 0;
    let totalCreditAccountReport = 0;
    let totalBalanceReport = 0;
    // Foreign-currency report-level accumulators (for the grand-total row).
    let totalFcDebitReport = 0;
    let totalFcCreditReport = 0;
    let totalFcBalanceReport = 0;

    const handleShowDetailsTransactions = (value) => {
        setDetailedTransactions(value);

    }

    const handleShowOpeningColumnsCheckbox = (value) => {
        setShowOpeningColumns(value);
    }

    const handleHideZeroRowsCheckbox = (value) => {
        setHideZeroRows(value);
    }

    const handleShow0CardsCheckbox = (value) => {
        setShowAccountCardsWithZeros(value);
        setShowWithZeros(value);
    }

    const handleShowAccountCodeIdCheckbox = (value) => {
        setShowAccountCodeId(value);
    }

    const formatAccountCardLabel = (id, name) => {
        const accountName = name || '';
        if (showAccountCodeId && id) {
            return `${id} ${accountName}`.trim();
        }
        return accountName;
    }

    const getTransactionField = (tr, fieldName) => {
        if (!tr) return undefined;
        const value = tr[fieldName];
        if (value !== undefined && value !== null) return value;
        const pascalCase = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
        return tr[pascalCase];
    };

    const transactionAmount = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return 0;
        return Math.round(n * 100) / 100;
    };

    /**
     * The trial balance keeps the raw trial-balance sign convention for every
     * group, including profit-and-loss groups (income as credit/negative,
     * expenses as debit/positive). Unlike the dedicated ProfitAndLossReportHtml
     * (which flips P&L groups to show income positive / expenses negative), the
     * trial balance does NOT flip the sign, so income stays negative here.
     */
    const isProfitAndLossGroup = (accountGroupName) =>
        profitAndLossNames.includes(accountGroupName);

    const profitAndLossDisplaySign = (accountGroupName) => 1;

    const displayBalance = (tr) =>
        transactionAmount(getTransactionField(tr, 'balance')) *
        profitAndLossDisplaySign(getTransactionField(tr, 'palCodeAccountGroup'));

    const sumDisplayBalance = (transactions) =>
        (transactions || []).reduce((acc, tr) => acc + displayBalance(tr), 0);

    // Match the values actually rendered in the report (net opening, not raw init debit/credit separately)
    const transactionHasActivity = (tr) => {
        const initDebit = transactionAmount(getTransactionField(tr, 'initDebitAccount'));
        const initCredit = transactionAmount(getTransactionField(tr, 'initCreditAccount'));
        const openingDiff = initDebit - initCredit;
        const shownOpeningDebit = openingDiff > 0 ? openingDiff : 0;
        const shownOpeningCredit = openingDiff < 0 ? -openingDiff : 0;

        return (
            shownOpeningDebit !== 0 ||
            shownOpeningCredit !== 0 ||
            transactionAmount(getTransactionField(tr, 'debitAccount')) !== 0 ||
            transactionAmount(getTransactionField(tr, 'creditAccount')) !== 0 ||
            transactionAmount(getTransactionField(tr, 'balance')) !== 0
        );
    };

    // A "reset" / zeroed card is one whose final balance (יתרה) is zero, even if
    // it had opening balances or movements during the period.
    const transactionHasNonZeroBalance = (tr) =>
        transactionAmount(getTransactionField(tr, 'balance')) !== 0;

    const getTransactionsForDisplay = (transactions) => {
        let result = showAccountCardsWithZeros ? transactions : transactions.filter(transactionHasNonZeroBalance);
        if (hideZeroRows) {
            result = result.filter(transactionHasActivity);
        }
        return result;
    };

    const filterResetCardTransactions = (transactions, includeResetCards) =>
        includeResetCards ? transactions : transactions.filter(transactionHasNonZeroBalance);

    const handleShowDetails = (value) => {
        setShowAccountCardsWithZeros(value);

    }

    const reportGenerated = useRef(false); // Ref to ensure generateReportHtml is called once
    let totalInitDebit = 0;
    let totalInitCredit = 0;
    let totalDebit = 0;
    let totalCredit = 0;
    let totalBalance1 = 0
    // Foreign-currency group-level accumulators (reset at each accounting-group boundary).
    let totalFcDebit = 0;
    let totalFcCredit = 0;
    let totalFcBalance = 0;

    let profitAndLossTotalInitDebit = 0;
    let profitAndLossTotalInitCredit = 0;
    let profitAndLossTotalDebit = 0;
    let profitAndLossTotalCredit = 0;
    let profitAndLossTotalBalance1 = 0
    let profitAndLossTotalFcDebit = 0;
    let profitAndLossTotalFcCredit = 0;
    let profitAndLossTotalFcBalance = 0;

    const initTotalValues = () => {
        totalInitDebitAccountReport = 0;
        totalInitCreditAccountReport = 0;
        totalDebitAccountReport = 0;
        totalCreditAccountReport = 0;
        totalBalanceReport = 0;

        totalInitDebit = 0;
        totalInitCredit = 0;
        totalDebit = 0;
        totalCredit = 0;
        totalBalance1 = 0;
        totalFcDebit = 0;
        totalFcCredit = 0;
        totalFcBalance = 0;
        totalFcDebitReport = 0;
        totalFcCreditReport = 0;
        totalFcBalanceReport = 0;

        profitAndLossTotalInitDebit = 0;
        profitAndLossTotalInitCredit = 0;
        profitAndLossTotalDebit = 0;
        profitAndLossTotalCredit = 0;
        profitAndLossTotalBalance1 = 0
        profitAndLossTotalFcDebit = 0;
        profitAndLossTotalFcCredit = 0;
        profitAndLossTotalFcBalance = 0;
    }

    useEffect(() => {
        setShowWithZeros(showAccountCardsWithZeros);
    }, [showAccountCardsWithZeros]);

    useEffect(() => {

        if (data) {
            const palCodeAccountGroupDict = {};
            const newProfitAndLossNames = [];

            data.transaction.forEach(item => {
                const accountGroupName = item.palCodeAccountGroup;
                const palCodeType = item.palCodeType;

                // The profit & loss section contains only תוצאתי (result account) palcodes.
                if (palCodeType === Constants.ResultAccountName) {
                    if (!newProfitAndLossNames.includes(accountGroupName)) {
                        newProfitAndLossNames.push(accountGroupName);
                    }
                }

                if (!palCodeAccountGroupDict[accountGroupName]) {
                    palCodeAccountGroupDict[accountGroupName] = {
                        name: accountGroupName,
                        count: 0,
                        appeared: 0,
                        totalInitDebit: 0,
                        totalInitCredit: 0,
                        totalDebit: 0,
                        totalCredit: 0,
                        totalBalance: 0
                    };
                }
            });

            // Only update state if the profitAndLossNames array has changed
            if (JSON.stringify(newProfitAndLossNames) !== JSON.stringify(profitAndLossNames)) {
                setProfitAndLossNames(newProfitAndLossNames);
            }

            // Set the accounting groups
            setAccountingGroups(palCodeAccountGroupDict);
            setShowReport(true);
        }
    }, [data, showWithZeros]);

    // Call generateReportHtml once after accountingGroups is set
    useEffect(() => {
        if (!data?.transaction) return;
        initTotalValues();
        generateReportHtml();
        if (showReport) {
            setShowReport(false);
        }
    }, [showReport, showAccountCardsWithZeros, detailedTransactions, showOpeningColumns, hideZeroRows, viewAccountCard, viewPalcodes, showAccountCodeId, showPeriodComparison, periodComparisonData, trialBalanceType, selectedFromSegmentation, selectedToSegmentation]);




    useEffect(() => {
        setClientData(client);
    }, [client]);






    const handleRowClick = (event) => {
        try {

            // Decode the encoded JSON string
            const jsonString = decodeURIComponent(event.detail);


            // Parse the JSON string
            const transaction = JSON.parse(jsonString);


            // Proceed with your logic
            const accountCard = {
                cardId: transaction.accountCardId,
                name: transaction.accountCardName,
            };

            const palCode = {
                palCodeId: transaction.palCode,
            };

            handleSelect(accountCard, palCode);
        } catch (error) {
            // Handle any JSON parsing errors
            console.error("Failed to parse JSON from event.detail:", error, event.detail);
        }
    };


    useEffect(() => {


        // Attach event listener for the custom event
        window.addEventListener('rowClick', handleRowClick);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('rowClick', handleRowClick);
        };
    }, []);


    const createMainHeader = (extraClasses = "") => {
        const now = new Date();
        const currentTime = now.toLocaleTimeString("he-IL", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        });
        // Format date as DD/MM/YY instead of DD.MM.YY
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear().toString();
        const currentDate = `${day}/${month}/${year}`;

        return {
            currentDate,
            currentTime
        };
    };

    const calculateTotalPages = (filteredTransactions) => {
        if (!filteredTransactions || filteredTransactions.length === 0) {
            return 1;
        }

        // Let's try counting accounting group classifications instead of categories
        const uniqueAccountingGroups = [...new Set(filteredTransactions
            .map(tr => tr.accountingGroupClassification)
            .filter(group => group !== "NonAccountingGroupClassifaction"))];


        // Try using accounting groups instead of categories
        const totalPages = uniqueAccountingGroups.length + 1;


        return Math.max(1, totalPages);
    };

    const getTableNumericColumnCount = (secondPeriodData = null) => {
        let count = getPeriodNumericColumns();
        if (showPeriodComparison && secondPeriodData) {
            count += getPeriodNumericColumns();
        }
        return count;
    };

    const buildTableColGroup = (secondPeriodData = null) => {
        const numericCols = Array.from(
            { length: getTableNumericColumnCount(secondPeriodData) },
            () => '<col class="col-numeric" />'
        ).join('');
        return `<colgroup><col class="col-account" />${numericCols}</colgroup>`;
    };

    const getTableColSpan = (secondPeriodData = null) =>
        1 + getTableNumericColumnCount(secondPeriodData);

    const getTableLayoutClass = (secondPeriodData = null) => {
        if (!detailedTransactions) {
            return getTableNumericColumnCount(secondPeriodData) === 1
                ? ' tb-balance-only tb-balance-single'
                : ' tb-balance-only tb-balance-dual';
        }
        return ' tb-standard-columns';
    };

    const getContentWrapperClass = () => {
        if (trialBalanceType === 'Categories') return ' tb-mode-categories';
        if (trialBalanceType === 'PalCodes') return ' tb-mode-palcodes';
        if (trialBalanceType === 'AccountCards' || trialBalanceType === 'Projects') return ' tb-mode-accountcards';
        return '';
    };

    const wrapDoubleUnderline = (amount, { topLine = false } = {}) => `
  <span class="accounting-amount-underline-double">
    ${topLine ? `<span class="accounting-underline-lines accounting-underline-lines-top">
      <span class="accounting-underline-line"></span>
    </span>` : ''}
    <span class="accounting-amount-value">${amount}</span>
    <span class="accounting-underline-lines accounting-underline-lines-double">
      <span class="accounting-underline-line"></span>
      <span class="accounting-underline-line"></span>
    </span>
  </span>`;

    /* ---- Foreign-currency (מט"ח) display ---- */
    // FC columns are shown when a specific currency or "mixed" is selected.
    const showFc = selectedCurrency !== null && selectedCurrency !== undefined;
    // When a specific currency is selected (not "mixed"), show ONLY the foreign-currency
    // columns and hide all shekel columns (including the opening-balance י.פ. columns).
    const fcOnly = typeof selectedCurrency === 'number';
    // Label such as מט"ח ($) for a single currency, or generic מט"ח for the mixed view.
    const fcLabel = (() => {
        if (typeof selectedCurrency === 'number') {
            const codeName = Object.entries(Constants.ForeignCurrencyCode || {})
                .find(([, value]) => value === selectedCurrency)?.[0];
            const sym = codeName ? (Constants.ForeignCurrencySymbol?.[codeName] || codeName) : '';
            return sym ? `מט"ח (${sym})` : 'מט"ח';
        }
        return 'מט"ח';
    })();
    // Blank for null/undefined so rows without FC stay empty (mixed view), otherwise formatted.
    const fcCell = (v) => (v === null || v === undefined ? '' : beautifyNumber(v));
    const sumFcField = (transactions, fieldName) =>
        (transactions || []).reduce((acc, tr) => {
            const v = getTransactionField(tr, fieldName);
            return v === null || v === undefined ? acc : acc + transactionAmount(v);
        }, 0);

    // Number of numeric columns rendered for a single period.
    //  - not detailed: 1 (balance only)
    //  - detailed + opening columns: 5 (opening debit/credit, debit, credit, balance)
    //  - detailed without opening columns: 3 (debit, credit, balance)
    // Plus FC columns when a currency is active: 3 when detailed (חובה/זכות/יתרה מט"ח), else 1 (יתרה מט"ח).
    const getPeriodNumericColumns = () => {
        if (fcOnly) return detailedTransactions ? 3 : 1;
        let count = !detailedTransactions ? 1 : (showOpeningColumns ? 5 : 3);
        if (showFc) count += detailedTransactions ? 3 : 1;
        return count;
    };

    // Switch to landscape when the extra foreign-currency columns make the table wide:
    // detailed FC adds 3 columns per period, and period-comparison doubles the column count.
    const useLandscape = (fcOnly || showFc) && (detailedTransactions || showPeriodComparison);

    // Foreign-currency header cells appended after the shekel columns.
    const renderForeignCurrencyHeaderCells = () => {
        if (!showFc) return '';
        if (detailedTransactions) {
            return `
            <th class="td-header td-fc">חובה ${fcLabel}</th>
            <th class="td-header td-fc">זכות ${fcLabel}</th>
            <th class="td-header td-fc">יתרה ${fcLabel}</th>`;
        }
        return `<th class="td-header td-fc">יתרה ${fcLabel}</th>`;
    };

    // Render the column headers for a single period.
    const renderTrialBalanceHeaderCells = () => {
        if (fcOnly) return renderForeignCurrencyHeaderCells();
        if (!detailedTransactions) {
            return `<th class="td-header">יתרה</th>${renderForeignCurrencyHeaderCells()}`;
        }
        if (showOpeningColumns) {
            return `
            <th class="td-header">י. פ. חובה</th>
            <th class="td-header">י. פ. זכות</th>
            <th class="td-header">חובה</th>
            <th class="td-header">זכות</th>
            <th class="td-header">יתרה</th>${renderForeignCurrencyHeaderCells()}`;
        }
        return `
            <th class="td-header">חובה</th>
            <th class="td-header">זכות</th>
            <th class="td-header">יתרה</th>${renderForeignCurrencyHeaderCells()}`;
    };

    // Fold FC opening balance into debit/credit (balance already includes opening on the backend).
    const foldFcOpening = (fcDebit, fcCredit, fcBalance) => {
        const d = transactionAmount(fcDebit);
        const c = transactionAmount(fcCredit);
        const b = transactionAmount(fcBalance);
        if (b === 0 && d === 0 && c === 0) return { debit: d, credit: c, balance: b };
        const opening = b - d + c;
        const openingDebit = opening > 0 ? opening : 0;
        const openingCredit = opening < 0 ? -opening : 0;
        return {
            debit: d + openingDebit,
            credit: c + openingCredit,
            balance: b,
        };
    };

    // Render the numeric cells for a single period given already-netted amounts.
    // When opening columns are hidden, the opening debit/credit are folded into
    // the debit/credit columns. Pass { sum: true } for total rows so the values
    // are wrapped with the double-underline styling.
    // Pass { topLine: true } only for the accounting-group total so a line is drawn above the amount.
    const renderTrialBalanceNumericCells = (
        { openingDebit = 0, openingCredit = 0, debit = 0, credit = 0, balance = 0,
          fcDebit = null, fcCredit = null, fcBalance = null },
        { sum = false, topLine = false } = {}
    ) => {
        const cls = sum ? 'td-last-sum' : 'td-amount-cell';
        const fmt = sum
            ? (v) => wrapDoubleUnderline(beautifyNumber(v), { topLine })
            : (v) => beautifyNumber(v);
        // FC cells stay blank when the row has no FC value (mixed view); totals use the underline style.
        const fcFmt = (v) => {
            if (v === null || v === undefined) return '';
            return sum ? wrapDoubleUnderline(beautifyNumber(v), { topLine }) : beautifyNumber(v);
        };

        if (fcOnly) {
            const folded = foldFcOpening(fcDebit, fcCredit, fcBalance);
            if (detailedTransactions) {
                return `
            <td class="${cls} td-fc">${fcFmt(folded.debit)}</td>
            <td class="${cls} td-fc">${fcFmt(folded.credit)}</td>
            <td class="${cls} td-fc">${fcFmt(folded.balance)}</td>`;
            }
            return `<td class="${cls} td-fc">${fcFmt(folded.balance)}</td>`;
        }

        const fcCells = !showFc ? '' : (detailedTransactions
            ? `<td class="${cls} td-fc">${fcFmt(fcDebit)}</td><td class="${cls} td-fc">${fcFmt(fcCredit)}</td><td class="${cls} td-fc">${fcFmt(fcBalance)}</td>`
            : `<td class="${cls} td-fc">${fcFmt(fcBalance)}</td>`);
        if (!detailedTransactions) {
            return `<td class="${cls}">${fmt(balance)}</td>${fcCells}`;
        }
        if (showOpeningColumns) {
            return `
            <td class="${cls}">${fmt(openingDebit)}</td>
            <td class="${cls}">${fmt(openingCredit)}</td>
            <td class="${cls}">${fmt(debit)}</td>
            <td class="${cls}">${fmt(credit)}</td>
            <td class="${cls}">${fmt(balance)}</td>${fcCells}`;
        }
        return `
            <td class="${cls}">${fmt(debit + openingDebit)}</td>
            <td class="${cls}">${fmt(credit + openingCredit)}</td>
            <td class="${cls}">${fmt(balance)}</td>${fcCells}`;
    };

    const getTrialBalanceReportStyles = () => `
      * {
        box-sizing: border-box;
        font-family: Arial, Helvetica, sans-serif !important;
      }

      html, body,
      .trial-balance-report-preview,
      .trial-balance-report-preview *,
      .content-wrapper,
      .content-wrapper *,
      .content-wrapper *::before,
      .content-wrapper *::after {
        font-family: Arial, Helvetica, sans-serif !important;
      }

      html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: #1a1a1a;
        direction: rtl;
      }

      body {
        counter-reset: page;
        font-size: 13px;
        line-height: 1.4;
      }

      @page {
        size: ${useLandscape ? 'A4 landscape' : 'A4'};
        margin: 8mm 10mm 16mm 10mm;
        @top-left {
          content: "עמוד " counter(page) " מתוך " counter(pages);
          font-size: 10px;
          font-weight: 700;
          color: #333;
          vertical-align: bottom;
        }
      }

      .page-number::before { content: counter(page); }
      .total-pages::before { content: counter(pages); }

      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }

      .content-wrapper {
        border: 2px solid #0066cc;
        border-radius: 8px;
        padding: 16px 32px;
        margin: 20px auto;
        width: ${useLandscape ? '94%' : '68%'};
        max-width: ${useLandscape ? '94%' : '68%'};
        min-width: 0;
        box-sizing: border-box;
        background: #ffffff;
        overflow: visible;
        box-shadow: none;
      }

      .pageStyle {
        display: block;
        background: #ffffff;
        padding: 0;
        margin: 0 0 8px 0;
        page-break-inside: avoid;
      }

      .page-break {
        page-break-before: always;
      }

      .hoverTr {
        cursor: pointer;
      }

      .hoverTr:hover {
        background-color: #f7f7f7;
      }

      .content-wrapper.tb-mode-accountcards .hoverTr {
        transition: transform 0.2s ease, box-shadow 0.1s ease;
      }

      .content-wrapper.tb-mode-accountcards .hoverTr:hover {
        background-color: transparent;
        transform: translateY(-5px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      .report-page-header {
        margin-bottom: 4px;
      }

      .report-header-row {
        display: flex;
        flex-direction: row;
        direction: rtl;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding: 4px 0 4px;
        width: 100%;
      }

      .report-header-title-block {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        direction: rtl;
        text-align: right;
        flex: 1;
      }

      .report-title {
        margin: 0 0 6px 0;
        padding: 0;
        font-size: 24px;
        font-weight: 700;
        color: #000;
        line-height: 1.2;
        text-decoration: none;
        text-align: right;
        width: 100%;
      }

      .report-date-range {
        margin: 0;
        padding: 0;
        font-size: 13px;
        font-weight: 600;
        color: #222;
        text-decoration: none;
        text-align: right;
        width: 100%;
      }

      .report-header-client-block {
        text-align: left;
        direction: rtl;
        flex: 1;
        max-width: 45%;
        align-self: flex-start;
      }

      .report-client-name {
        margin: 0 0 4px 0;
        padding: 0;
        font-size: 15px;
        font-weight: 700;
        color: #000;
        text-align: left;
      }

      .report-client-case,
      .report-print-datetime {
        margin: 0 0 2px 0;
        padding: 0;
        font-size: 13px;
        font-weight: 600;
        color: #333;
        text-align: left;
      }

      .report-header-divider {
        border: none;
        border-top: 2px solid #000;
        margin: 4px 0 8px;
        width: 100%;
      }

      .report-meta-details {
        margin-top: 8px;
      }

      .report-date-comparison-block {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        justify-content: flex-start;
        direction: rtl;
        flex-wrap: nowrap;
        width: 100%;
        gap: 16px;
      }

      .report-date-comparison-block .report-date-range,
      .report-date-comparison-block .report-comparison-period {
        width: auto;
        flex: 0 0 auto;
        display: inline-block;
        margin: 0;
        white-space: nowrap;
      }

      .report-comparison-period {
        font-weight: 600;
      }

      .report-project-filter {
        margin-top: 4px;
        font-weight: 600;
      }

      .report-project-range {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        flex-direction: row;
        direction: rtl;
      }

      .palcodes-details {
        font-size: 12px;
        color: #333;
        text-align: right;
        margin: 2px 0;
      }

      .sum-details {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-end;
        margin-top: 4px;
        margin-bottom: 4px;
      }

      .accountingGroupClassification_accountingGroup_container {
        display: block;
        width: 100%;
        margin: 0;
      }

      .section-header-block {
        width: 100%;
        margin: 6px 0 4px;
        padding: 0 0 6px;
        box-sizing: border-box;
        border-bottom: 2px solid #000;
      }

      .section-header-row {
        display: flex;
        flex-direction: row;
        direction: rtl;
        justify-content: space-between;
        align-items: baseline;
        gap: 20px;
        flex-wrap: nowrap;
        width: 100%;
        box-sizing: border-box;
      }

      .section-header-row .sums-of-transactions {
        margin: 0;
        padding: 4px 0;
      }

      .section-header-row .section-header-classification,
      .section-header-row .section-header-group {
        text-align: right;
        flex-shrink: 0;
      }

      .combined-section-header {
        width: 100%;
        display: flex;
        flex-direction: column;
        direction: rtl;
        align-items: flex-start;
      }

      .sums-of-transactions,
      .section-header-title {
        font-size: 16px;
        font-weight: 700;
        color: #000;
        text-align: right;
        text-decoration: none;
        margin: 0;
      }

      .section-header-title {
        margin-top: 4px;
      }

      .section-header-ag-label {
        width: 100%;
        text-align: right;
        margin: 0;
        padding: 2px 0;
        font-size: 18px;
        font-weight: 700;
        color: #000;
        line-height: 1.2;
      }

      .combined-section-header-continued {
        margin-top: 4px;
      }

      .section-header-underline {
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 4px;
        width: 100%;
        text-align: right;
      }

      .accounting-amount-underline-double {
        display: inline-block;
        min-width: 80px;
        max-width: 100%;
        text-align: right;
        vertical-align: bottom;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .td-last-sum .accounting-amount-underline-double {
        display: block;
        width: 100%;
        min-width: 0;
        max-width: 100%;
        box-sizing: border-box;
      }

      .td-last-sum .accounting-underline-lines-double {
        position: absolute;
        left: 6px;
        right: 6px;
        bottom: 0;
        width: auto;
        margin-top: 0;
        padding: 0;
        box-sizing: border-box;
      }

      .td-last-sum .accounting-underline-lines-top {
        position: relative;
        width: auto;
        margin: 0 -4px 2px -8px;
        padding: 0;
        box-sizing: border-box;
      }

      .accounting-amount-value {
        display: block;
        line-height: 1.25;
        white-space: nowrap;
      }

      .accounting-underline-lines {
        display: block;
        width: 100%;
        margin-top: 2px;
      }

      .accounting-underline-line {
        display: block;
        width: 100%;
        height: 0;
        border: none;
        border-top: 1px solid #000;
        margin: 0;
        padding: 0;
        line-height: 0;
        font-size: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .accounting-underline-lines-double .accounting-underline-line + .accounting-underline-line {
        margin-top: 2px;
        margin-bottom: 2px;
      }

      tr.tr-final-row-palcode .td-last-sum,
      tr.tr-final-row-palcode-category .td-last-sum,
      tr.pal-code-account-group-total-row .td-last-sum,
      tr.tr-final-row-palcode-Report .td-last-sum {
        padding-bottom: 10px;
        vertical-align: bottom;
      }

      .table {
        width: 100%;
        max-width: 100%;
        border-collapse: collapse;
        border-spacing: 0;
        margin: 0 0 12px 0;
        page-break-inside: auto;
        font-size: 13px;
        table-layout: fixed;
        direction: rtl;
      }

      .table col.col-account {
        width: 50%;
      }

      .table col.col-numeric {
        width: 50%;
      }

      .table.tb-balance-single col.col-account {
        width: 78%;
      }

      .table.tb-balance-single col.col-numeric {
        width: 22%;
      }

      .table.tb-balance-dual col.col-account {
        width: 64%;
      }

      .table.tb-balance-dual col.col-numeric {
        width: 18%;
      }

      .table.tb-standard-columns col.col-account {
        width: 28%;
      }

      .table.tb-standard-columns col.col-numeric {
        width: auto;
      }

      .table.tb-balance-only thead .td-header::after {
        content: none;
        display: none;
      }

      .table th,
      .table td {
        border-left: none;
        border-right: none;
        border-top: none;
        padding: 4px 10px;
        vertical-align: middle;
        text-align: right;
        background: #ffffff;
        box-sizing: border-box;
        overflow: hidden;
      }

      .table.tb-balance-single th:first-child,
      .table.tb-balance-single td:first-child {
        width: 78%;
        max-width: 78%;
      }

      .table.tb-balance-single th:last-child,
      .table.tb-balance-single td:last-child {
        width: 22%;
        max-width: 22%;
      }

      .table.tb-balance-dual th:first-child,
      .table.tb-balance-dual td:first-child {
        width: 64%;
        max-width: 64%;
      }

      .table.tb-standard-columns th:first-child,
      .table.tb-standard-columns td:first-child {
        width: 28%;
        max-width: 28%;
      }

      .table thead th {
        border-bottom: none;
        border-top: none;
        font-weight: 700;
        font-size: 14px;
        background: #ffffff;
      }

      .table th:first-child,
      .table td:first-child {
        text-align: right;
        word-wrap: break-word;
        overflow-wrap: anywhere;
      }

      .td-header,
      .td-header-1 {
        font-size: 13px;
        font-weight: 700;
        text-align: right !important;
        text-decoration: none;
        color: #000;
        border-bottom: none;
        padding-bottom: 2px;
        vertical-align: bottom;
        position: relative;
      }

      .table thead .td-header::after {
        content: '';
        display: block;
        position: absolute;
        left: 6px;
        right: 6px;
        bottom: 0;
        height: 0;
        border: none;
        border-top: 1px solid #000;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .table thead .td-header-1::after {
        content: none;
        display: none;
      }

      .overflowed {
        font-size: 15px;
        font-weight: 700;
        color: #000;
        text-align: right;
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 3px;
        padding-top: 8px;
        padding-bottom: 4px;
        background: #fafafa;
      }

      .td-last,
      .td-amount-cell,
      .td-last-sum,
      .td-last-sum2 {
        font-size: 12px;
        text-align: left;
        direction: ltr;
        white-space: nowrap;
        padding-left: 12px;
        padding-right: 10px;
      }

      .td-account-cell,
      .table td:first-child.total-palcode,
      .table td:first-child.total-palcode-final {
        font-size: 12px;
        direction: rtl;
        text-align: right;
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: anywhere;
        padding-right: 12px;
        padding-left: 10px;
        padding-bottom: 4px;
      }

      .table td.total-palcode,
      .table td.total-palcode-final {
        padding-right: 12px;
        padding-left: 10px;
      }

      tr.tr-final-row-palcode td.td-last-sum,
      tr.tr-final-row-palcode-category td.td-last-sum,
      tr.pal-code-account-group-total-row td.td-last-sum,
      tr.tr-final-row-palcode-Report td.td-last-sum {
        padding-left: 14px;
        padding-right: 10px;
      }

      .td-amount-cell {
        padding: 3px 8px;
      }

      .tr {
        font-size: 13px;
      }

      tr.tr-final-row-palcode td {
        font-weight: 700;
        padding-top: 6px;
        padding-bottom: 6px;
      }

      tr.tr-final-row-palcode-category td {
        font-weight: 700;
        padding-top: 6px;
        padding-bottom: 6px;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      tr.pal-code-account-group-total-row td {
        font-weight: 700;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      tr.tr-final-row-palcode-Report td {
        font-weight: 700;
        background-color: #e8e8e8;
        padding-top: 4px;
        padding-bottom: 4px;
        vertical-align: bottom;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      tr.tr-final-row-palcode-Report td.total-palcode,
      tr.tr-final-row-palcode-Report td.total-palcode-final {
        vertical-align: bottom;
        padding-bottom: 10px;
      }

      tr.tr-final-row-palcode-Report-last td {
        padding-bottom: 14px;
      }

      tr.tr-final-summary-spacer td {
        height: 6px;
        padding: 0 !important;
        border: none !important;
        background: transparent !important;
        line-height: 0;
        font-size: 0;
      }

      .total-palcode {
        font-weight: 700;
        font-size: 13px;
        color: #000;
        text-align: left !important;
        direction: rtl !important;
      }

      .total-palcode-final {
        font-weight: 700;
        font-size: 14px;
        color: #000;
        text-decoration: none;
      }

      .td-last-sum,
      .td-last-sum-1 {
        text-align: left;
        direction: ltr;
        font-weight: 700;
        // font-size: 13px;
        white-space: nowrap;
        position: relative;
        vertical-align: bottom;
        padding-bottom: 10px;
      }

      .td-last-sum::before,
      .td-last-sum::after,
      .td-last-sum-1::before,
      .td-last-sum-1::after,
      .td-last-sum2::before,
      .td-last-sum2::after,
      .num-total::before,
      .num-total::after {
        content: none;
        display: none;
      }

      .num-total {
        font-weight: 700;
        direction: ltr;
      }

      h1 { color: #000; text-align: right; }
      p { color: #333; }

      .rt1, .rt2, .det-small, .first, .det, .num, .normal-num, .underlined {
        color: #000;
      }

      .upper-name-div,
      .left-header-info,
      .right-header-info,
      #header,
      .middle-details {
        display: none;
      }

      @media print {
        @page {
          size: ${useLandscape ? 'A4 landscape' : 'A4'};
          margin: 8mm 10mm 16mm 10mm !important;
          @top-left {
            content: "עמוד " counter(page) " מתוך " counter(pages);
            font-size: 10px;
            font-weight: 700;
            color: #333;
            vertical-align: bottom;
          }
        }

        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          background: #ffffff !important;
          overflow: visible !important;
          direction: rtl;
        }

        .content-wrapper,
        .pageStyle {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          overflow: visible !important;
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          min-width: 0 !important;
        }

        .pageStyle {
          display: block !important;
          page-break-inside: auto !important;
        }

        .report-page-header,
        .report-header-row,
        .report-meta-details,
        .combined-section-header,
        .section-header-block {
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
          box-sizing: border-box;
        }

        .hoverTr,
        .hoverTr:hover {
          cursor: default !important;
          background: transparent !important;
          transform: none !important;
          box-shadow: none !important;
        }

        .page-break {
          display: none;
        }

        .table {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          font-size: 12px !important;
          border-collapse: collapse !important;
          border-spacing: 0 !important;
          table-layout: fixed !important;
        }

        .table.tb-balance-only,
        .table.tb-standard-columns {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }

        .table.tb-balance-single col.col-numeric {
          width: 22% !important;
        }

        .table.tb-balance-dual col.col-numeric {
          width: 18% !important;
        }

        .table.tb-standard-columns col.col-numeric {
          width: 12% !important;
        }

        .table.tb-balance-only col.col-account,
        .table.tb-standard-columns col.col-account {
          width: auto !important;
        }

        .table.tb-balance-only thead .td-header::after {
          content: none !important;
          display: none !important;
        }

        .table th,
        .table td {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          overflow: visible !important;
          word-wrap: break-word;
          overflow-wrap: anywhere;
          padding: 2px 4px !important;
        }

        .palcodes-details {
          font-size: 11px !important;
          line-height: 1.25 !important;
        }

        .report-title { font-size: 22px !important; }
        .report-date-range { font-size: 12px !important; }
        .report-client-name { font-size: 14px !important; }
        .report-client-case,
        .report-print-datetime { font-size: 11px !important; }

        .sums-of-transactions,
        .section-header-title { font-size: 13px !important; }

        .section-header-ag-label { font-size: 13px !important; }

        .overflowed { font-size: 12px !important; }

        .td-header,
        .td-header-1 { font-size: 12px !important; }

        .table thead .td-header::after {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        td, .td-last, .td-amount-cell, .td-last-sum, .td-last-sum2 {
          font-size: 12px !important;
        }

        .total-palcode { font-size: 12px !important; }
        .total-palcode-final { font-size: 13px !important; }

        tr.tr-final-row-palcode-category td,
        tr.pal-code-account-group-total-row td,
        tr.tr-final-row-palcode-Report td {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        tr.tr-final-row-palcode-Report td.total-palcode,
        tr.tr-final-row-palcode-Report td.total-palcode-final {
          vertical-align: bottom !important;
          padding-top: 4px !important;
          padding-bottom: 12px !important;
          padding-left: 4px !important;
          padding-right: 4px !important;
        }

        tr.tr-final-row-palcode-Report-last td {
          padding-bottom: 16px !important;
        }

        tr.tr-final-row-palcode-Report-last td.td-last-sum {
          padding-bottom: 16px !important;
        }

        tr.tr-final-summary-spacer td {
          height: 8px !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
        }

        .accounting-amount-underline-double,
        .accounting-underline-line {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .accounting-underline-lines-double .accounting-underline-line + .accounting-underline-line {
          margin-top: 2px !important;
          margin-bottom: 0 !important;
        }

        .accounting-amount-underline-double {
          min-width: 80px !important;
          width: auto !important;
          max-width: 100%;
        }

        .td-last-sum .accounting-amount-underline-double {
          display: block !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
        }

        .td-last-sum,
        .td-last-sum-1 {
          padding-bottom: 12px !important;
        }

        tr.tr-final-row-palcode td.td-last-sum,
        tr.tr-final-row-palcode-category td.td-last-sum,
        tr.pal-code-account-group-total-row td.td-last-sum,
        tr.tr-final-row-palcode-Report td.td-last-sum {
          padding-bottom: 10px !important;
        }

        .td-last-sum .accounting-amount-value {
          margin-bottom: 2px !important;
        }

        .td-last-sum .accounting-underline-lines-top {
          position: static !important;
          width: 100% !important;
          margin: 0 0 2px 0 !important;
          padding: 0 !important;
        }

        .td-last-sum .accounting-underline-lines-double {
          position: static !important;
          left: auto !important;
          right: auto !important;
          bottom: auto !important;
          width: 100% !important;
          padding: 0 !important;
          margin-top: 2px !important;
        }

        .sum-details {
          margin-top: 4px !important;
          margin-bottom: 4px !important;
        }

        .report-date-comparison-block {
          flex-wrap: nowrap !important;
        }

        .report-date-comparison-block .report-date-range {
          width: auto !important;
          display: inline-block !important;
        }
      }
    `;

    const generateReportHtml = () => {
        // Determine which data to use for the first period
        let firstPeriodData, secondPeriodData;

        if (showPeriodComparison && periodComparisonData.data1 && periodComparisonData.data2) {
            firstPeriodData = periodComparisonData.data1;
            secondPeriodData = periodComparisonData.data2;
        } else {
            firstPeriodData = data;
            secondPeriodData = null;
        }

        let filteredTransactions = filterResetCardTransactions(
            firstPeriodData.transaction,
            showAccountCardsWithZeros
        );

        // When a specific currency is selected, only show rows that carry foreign-currency
        // activity (debit/credit/balance) in that currency. Mixed/Shekel views are unaffected.
        if (typeof selectedCurrency === 'number') {
            filteredTransactions = filteredTransactions.filter(tr =>
                transactionAmount(getTransactionField(tr, 'foreignCurrencyDebit')) !== 0 ||
                transactionAmount(getTransactionField(tr, 'foreignCurrencyCredit')) !== 0 ||
                transactionAmount(getTransactionField(tr, 'foreignCurrencyBalance')) !== 0
            );
        }

        const uniquePalCodeCategories = [...new Set(filteredTransactions
            .map(tr => tr.palCodeCategory)
            .filter(category => category !== "NonPalCodeCategory"))];

        const uniquePalCodes = [...new Set(filteredTransactions
            .map(tr => tr.palCode)
            .filter(code => code !== "NonPalCode"))];

        // Get current date and time
        const { currentDate, currentTime } = createMainHeader();

        // Calculate total pages dynamically
        const totalPages = calculateTotalPages(filteredTransactions);
        const currentPage = 1;

        const reportTitle = trialBalanceType === "Categories" || trialBalanceType === "PalCodes" ? "טיוטת מאזן" : "דוח מאזן בוחן יתרות";

        const dateRangeHtml = showPeriodComparison && secondPeriodData
            ? `<div class="report-date-comparison-block">
                    <span class="report-date-range report-comparison-period">מחודש ${firstPeriodData.startDate} עד חודש ${firstPeriodData.endDate}</span>
                    <span class="report-date-range report-comparison-period">מחודש ${secondPeriodData.startDate} עד חודש ${secondPeriodData.endDate}</span>
                </div>`
            : `<p class="report-date-range">מחודש ${firstPeriodData.startDate} עד חודש ${firstPeriodData.endDate}</p>`;

        // Generate the main content once
        let content = `
                <div class="report-header-row">
                    <div class="report-header-title-block">
                        <h1 class="report-title">${reportTitle}</h1>
                        ${dateRangeHtml}
                    </div>
                    <div class="report-header-client-block">
                        <p class="report-client-name">${data.clientName}</p>
                        <p class="report-client-case">${getCaseNumberLabel(client?.caseType ?? data?.caseType)}: ${data.clientCase}</p>
                        <p class="report-print-datetime">תאריך הדפסה: ${currentTime} - ${currentDate}</p>
                    </div>
                </div>  
                <div class="report-page-header pageStyle">

                    <hr class="report-header-divider" />
                    <div class="report-meta-details sum-details">
                        <div class="palcodes-details">
                            מסעיף: ${uniquePalCodeCategories[0] || ""} - עד סעיף: ${uniquePalCodeCategories[uniquePalCodeCategories.length - 1] || ""}
                        </div>
                        <div class="palcodes-details">
                            מסיווג: ${uniquePalCodes[0] || ""} - עד סיווג: ${uniquePalCodes[uniquePalCodes.length - 1] || ""}
                        </div>
                        ${trialBalanceType === 'Projects' ? (() => {
            let projectLine = '';
            if (!selectedFromSegmentation && !selectedToSegmentation) {
                projectLine = escapeHtmlAttr(t('TrialBalanceProjectFilterAllSegmented'));
            } else {
                const fromL = selectedFromSegmentation ? escapeHtmlAttr(selectedFromSegmentation.label) : '—';
                const toL = selectedToSegmentation ? escapeHtmlAttr(selectedToSegmentation.label) : '—';
                projectLine = `<span class="report-project-range"><span>${escapeHtmlAttr(t('FromProject'))}: <span dir="ltr">${fromL}</span></span><span>${escapeHtmlAttr(t('ToProject'))}: <span dir="ltr">${toL}</span></span></span>`;
            }
            return `<div class="palcodes-details report-project-filter" dir="rtl"><span>${escapeHtmlAttr(t('ByProjects'))}:</span> ${projectLine}</div>`;
        })() : ''}
                    </div>
                </div>`;
        content += generateAccountingGroupClassificationSections(filteredTransactions, uniquePalCodeCategories, uniquePalCodes, secondPeriodData);
        const diff1 = profitAndLossTotalInitDebit - profitAndLossTotalInitCredit;
        const diff = totalInitDebitAccountReport - totalInitCreditAccountReport;

        // Calculate comparison totals if in comparison mode
        let comparisonFinalTotals = '';
        let comparisonDiff = 0;
        let comparisonTotalBalanceReport = 0;
        let comparisonTotalDebitAccountReport = 0;
        let comparisonTotalCreditAccountReport = 0;
        let comparisonTotalFcDebitReportFinal = 0;
        let comparisonTotalFcCreditReportFinal = 0;
        let comparisonTotalFcBalanceReportFinal = 0;

        if (showPeriodComparison && secondPeriodData) {
            // Calculate comparison totals for the second period
            let comparisonProfitAndLossTotalInitDebit = 0;
            let comparisonProfitAndLossTotalInitCredit = 0;
            let comparisonProfitAndLossTotalDebit = 0;
            let comparisonProfitAndLossTotalCredit = 0;
            let comparisonProfitAndLossTotalBalance = 0;
            let comparisonTotalInitDebitAccountReport = 0;
            let comparisonTotalInitCreditAccountReport = 0;
            let comparisonTotalFcDebitReport = 0;
            let comparisonTotalFcCreditReport = 0;
            let comparisonTotalFcBalanceReport = 0;
            let comparisonProfitAndLossTotalFcDebit = 0;
            let comparisonProfitAndLossTotalFcCredit = 0;
            let comparisonProfitAndLossTotalFcBalance = 0;

            // Process all transactions in the second period to calculate totals
            secondPeriodData.transaction.forEach(tr => {
                comparisonTotalInitDebitAccountReport += tr.initDebitAccount;
                comparisonTotalInitCreditAccountReport += tr.initCreditAccount;
                comparisonTotalDebitAccountReport += tr.debitAccount;
                comparisonTotalCreditAccountReport += tr.creditAccount;
                comparisonTotalBalanceReport += tr.balance;
                if (showFc) {
                    comparisonTotalFcDebitReport += transactionAmount(getTransactionField(tr, 'foreignCurrencyDebit'));
                    comparisonTotalFcCreditReport += transactionAmount(getTransactionField(tr, 'foreignCurrencyCredit'));
                    comparisonTotalFcBalanceReport += transactionAmount(getTransactionField(tr, 'foreignCurrencyBalance'));
                }

                // Check if this transaction is in profit and loss
                if (profitAndLossNames.includes(tr.palCodeAccountGroup)) {
                    comparisonProfitAndLossTotalInitDebit += tr.initDebitAccount;
                    comparisonProfitAndLossTotalInitCredit += tr.initCreditAccount;
                    comparisonProfitAndLossTotalDebit += tr.debitAccount;
                    comparisonProfitAndLossTotalCredit += tr.creditAccount;
                    comparisonProfitAndLossTotalBalance += tr.balance;
                    if (showFc) {
                        comparisonProfitAndLossTotalFcDebit += transactionAmount(getTransactionField(tr, 'foreignCurrencyDebit'));
                        comparisonProfitAndLossTotalFcCredit += transactionAmount(getTransactionField(tr, 'foreignCurrencyCredit'));
                        comparisonProfitAndLossTotalFcBalance += transactionAmount(getTransactionField(tr, 'foreignCurrencyBalance'));
                    }
                }
            });

            const comparisonDiff1 = comparisonProfitAndLossTotalInitDebit - comparisonProfitAndLossTotalInitCredit;
            comparisonDiff = comparisonTotalInitDebitAccountReport - comparisonTotalInitCreditAccountReport;

            comparisonFinalTotals = renderTrialBalanceNumericCells({
                openingDebit: comparisonDiff1 > 0 ? comparisonDiff1 : 0,
                openingCredit: comparisonDiff1 < 0 ? -comparisonDiff1 : 0,
                debit: comparisonProfitAndLossTotalDebit,
                credit: comparisonProfitAndLossTotalCredit,
                balance: comparisonProfitAndLossTotalBalance,
                fcDebit: showFc ? comparisonProfitAndLossTotalFcDebit : null,
                fcCredit: showFc ? comparisonProfitAndLossTotalFcCredit : null,
                fcBalance: showFc ? comparisonProfitAndLossTotalFcBalance : null,
            }, { sum: true });

            comparisonTotalFcDebitReportFinal = comparisonTotalFcDebitReport;
            comparisonTotalFcCreditReportFinal = comparisonTotalFcCreditReport;
            comparisonTotalFcBalanceReportFinal = comparisonTotalFcBalanceReport;
        }

        // Define the final summary row to inject
        const finalSummaryColSpan = getTableColSpan(secondPeriodData);
        const finalSummaryRow = `
            <tr class="tr-final-row-palcode-Report">
                <td class="total-palcode total-palcode-final">סה''כ רווח והפסד בדוח: </td>
                ${renderTrialBalanceNumericCells({
                    openingDebit: diff1 > 0 ? diff1 : 0,
                    openingCredit: diff1 < 0 ? -diff1 : 0,
                    debit: profitAndLossTotalDebit,
                    credit: profitAndLossTotalCredit,
                    balance: profitAndLossTotalBalance1,
                    fcDebit: showFc ? profitAndLossTotalFcDebit : null,
                    fcCredit: showFc ? profitAndLossTotalFcCredit : null,
                    fcBalance: showFc ? profitAndLossTotalFcBalance : null,
                }, { sum: true })}
                ${comparisonFinalTotals}
            </tr>
            <tr class="tr-final-row-palcode-Report tr-final-row-palcode-Report-last">
                <td class="total-palcode total-palcode-final">סה''כ בדוח: </td>
                ${renderTrialBalanceNumericCells({
                    openingDebit: diff > 0 ? diff : 0,
                    openingCredit: diff < 0 ? -diff : 0,
                    debit: totalDebitAccountReport,
                    credit: totalCreditAccountReport,
                    balance: totalBalanceReport,
                    fcDebit: showFc ? totalFcDebitReport : null,
                    fcCredit: showFc ? totalFcCreditReport : null,
                    fcBalance: showFc ? totalFcBalanceReport : null,
                }, { sum: true })}
                ${showPeriodComparison && secondPeriodData ? renderTrialBalanceNumericCells({
                    openingDebit: comparisonDiff > 0 ? comparisonDiff : 0,
                    openingCredit: comparisonDiff < 0 ? -comparisonDiff : 0,
                    debit: comparisonTotalDebitAccountReport,
                    credit: comparisonTotalCreditAccountReport,
                    balance: comparisonTotalBalanceReport,
                    fcDebit: showFc ? comparisonTotalFcDebitReportFinal : null,
                    fcCredit: showFc ? comparisonTotalFcCreditReportFinal : null,
                    fcBalance: showFc ? comparisonTotalFcBalanceReportFinal : null,
                }, { sum: true }) : ''}
            </tr>
            <tr class="tr-final-summary-spacer" aria-hidden="true">
                <td colspan="${finalSummaryColSpan}"></td>
            </tr>
        `;



        // Inject final summary rows before the last table body closes
        const lastTbodyIndex = content.lastIndexOf('</tbody>');
        if (lastTbodyIndex !== -1) {
            content = content.slice(0, lastTbodyIndex) + finalSummaryRow + content.slice(lastTbodyIndex);
        } else {
            content = content.slice(0, -60);
            content += finalSummaryRow;
            content += `
            </tbody>
        </table>
    </div>`;
        }
        const reportHtml = `
            <!DOCTYPE html>
            <html lang="he">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                ${getTrialBalanceReportStyles()}
                </style>
            </head>
            <body dir="rtl" class="trial-balance-report-preview">
                <div class="content-wrapper${getContentWrapperClass()}">
                    ${content}
                </div>
            </body>
            </html>
        `;

        setReportHtmlContent(reportHtml);
    };

    const updateNonPalCodeValues = (transactions) => {
        return transactions.map(transaction => {
            return {
                ...transaction,
                palCodeType: transaction.palCodeType === 'NonPalCodeType' ? t('NonPalCodeType') : transaction.palCodeType,
                palCodeCategory: transaction.palCodeCategory === 'NonPalCodeCategory' ? t('NonPalCodeCategory') : transaction.palCodeCategory,
                palCodeAccountGroup: transaction.palCodeAccountGroup === 'NonPalCodeAccountGroup' ? t('NonPalCodeAccountGroup') : transaction.palCodeAccountGroup,
                accountingGroupClassification: transaction.accountingGroupClassification === 'NonAccountingGroupClassifaction' ? t('NonAccountingGroupClassifaction') : transaction.accountingGroupClassification
            };
        });
    };



    const loadSegmentationProjects = useCallback(async () => {
        if (!client?.caseId || !token) return;
        setLoadingSegmentations(true);
        try {
            const res = await ProjectsService.getProjects(client.caseId, token);
            if (!res.ok) {
                toast.error(t('ErrorRetrievingProjects'));
                return;
            }
            const raw = await res.json();
            const list = Array.isArray(raw) ? raw : [];
            const mapped = list
                .map((p) => ({
                    id: String(p.projectId ?? p.id ?? ''),
                    code: p.code ?? '',
                    name: p.name ?? '',
                    label: `${p.code ?? ''} — ${p.name ?? ''}`,
                }))
                .filter((p) => p.id)
                .sort((a, b) => compareProjectCodes(a, b));
            setSegmentationProjects(mapped);
        } catch (e) {
            console.error(e);
            toast.error(t('ErrorRetrievingProjects'));
        } finally {
            setLoadingSegmentations(false);
        }
    }, [client?.caseId, token, t]);

    const handleFromSegmentationChange = async (_, newVal) => {
        if (trialBalanceType !== 'Projects') return;
        let from = newVal;
        let to = selectedToSegmentation;
        if (from && to && compareProjectCodes(from, to) > 0) {
            toast.info(t('ProjectRangeAdjustedToCodeOrder'));
            const tmp = from;
            from = to;
            to = tmp;
            setSelectedToSegmentation(to);
        }
        setSelectedFromSegmentation(from);
        await notifySegmentationFilterChange({
            trialBalanceType: 'Projects',
            ...(from?.id ? { fromSegmentationId: from.id } : {}),
            ...(to?.id ? { toSegmentationId: to.id } : {}),
        });
    };

    const handleToSegmentationChange = async (_, newVal) => {
        if (trialBalanceType !== 'Projects') return;
        let to = newVal;
        let from = selectedFromSegmentation;
        if (from && to && compareProjectCodes(from, to) > 0) {
            toast.info(t('ProjectRangeAdjustedToCodeOrder'));
            const tmp = to;
            to = from;
            from = tmp;
            setSelectedFromSegmentation(from);
        }
        setSelectedToSegmentation(to);
        await notifySegmentationFilterChange({
            trialBalanceType: 'Projects',
            ...(from?.id ? { fromSegmentationId: from.id } : {}),
            ...(to?.id ? { toSegmentationId: to.id } : {}),
        });
    };

    const handleTrialBalanceTypeChange = async (event, newAlignment) => {
        if (newAlignment === 'AccountCards' || newAlignment === 'Projects') {
            setViewAccountCard(true);
            setViewPalcodes(true);
        }
        if (newAlignment === 'PalCodes') {
            setViewPalcodes(true);
            setViewAccountCard(false);
        }
        if (newAlignment === 'Categories') {
            setViewPalcodes(false);
            setViewAccountCard(false);
        }

        if (newAlignment === null) {
            return;
        }

        if (trialBalanceType === 'Projects' && newAlignment !== 'Projects') {
            await notifySegmentationFilterChange(null);
        }

        setTrialBalanceType(newAlignment);

        if (newAlignment === 'Projects') {
            setSelectedFromSegmentation(null);
            setSelectedToSegmentation(null);
            void loadSegmentationProjects();
            await notifySegmentationFilterChange({ trialBalanceType: 'Projects' });
        }
    };

    // Monthly Report Modal handlers
    const handleOpenMonthlyReportModal = () => {
        setShowMonthlyReportModal(true);
    };

    const handleCloseMonthlyReportModal = () => {
        setShowMonthlyReportModal(false);
    };

    // Period comparison handlers
    const handleFetchComparisonDataWithRebuild = async () => {
        try {
            await handleFetchComparisonData();
        } catch (error) {
            console.error('Error in comparison data fetch:', error);
        }
    };

    const handleCloseComparisonWithRebuild = () => {
        handleCloseComparison();
    };

    const generateAccountingGroupClassificationSections = (transactions, uniquePalCodeCategories, uniquePalCodes, secondPeriodData = null) => {


        let content = '';
        const groupedByAccountingGroupClassification = groupBy(transactions, 'accountingGroupClassification');


        Object.keys(groupedByAccountingGroupClassification).forEach(accountingGroupClassification => {


            const transactionsForAccountingGroupClassification = groupedByAccountingGroupClassification[accountingGroupClassification];



            const groupedByPalCodeCategory = groupBy(transactionsForAccountingGroupClassification, 'palCodeCategory');



            const palCodeAccountGroup = transactionsForAccountingGroupClassification[0]?.palCodeAccountGroup || '';

            // Process each transaction and replace "Non" values with translations



            let headerHtml = `
               
               
            `;
            const categories = Object.keys(groupedByPalCodeCategory);
            Object.keys(groupedByPalCodeCategory).forEach((palCodeCategory, index) => {
                //    if (accountingGroupClassification === 'נכסים') {

                const currentCategory = groupedByPalCodeCategory[palCodeCategory];

                const nextCategoryKey = categories[index + 1];
                const nextCategory = groupedByPalCodeCategory[nextCategoryKey];

                // 
                // 
                // 
                // 
                // }

                let printAccountingGroup = false;
                printAccountingGroup = !nextCategory || (currentCategory[0]?.palCodeAccountGroup !== nextCategory[0]?.palCodeAccountGroup);

                const transactionsForPalCodeCategory = groupedByPalCodeCategory[palCodeCategory];

                const safeId = palCodeCategory;

                const categoryLabel = palCodeCategory === 'NonPalCodeCategory'
                    ? t('NonPalCodeCategory')
                    : palCodeCategory;
                const groupLabelRaw = transactionsForPalCodeCategory[0]?.palCodeAccountGroup || palCodeAccountGroup;
                const groupLabel = groupLabelRaw === 'NonPalCodeAccountGroup'
                    ? t('NonPalCodeAccountGroup')
                    : groupLabelRaw;
                const combinedSectionLabel = groupLabel
                    ? `${categoryLabel} - ${groupLabel}`
                    : categoryLabel;
                const classificationLabel = accountingGroupClassification === 'NonAccountingGroupClassifaction'
                    ? t('NonAccountingGroupClassifaction')
                    : accountingGroupClassification;
                const isFirstCategoryInClassification = index === 0;

                headerHtml = isFirstCategoryInClassification
                    ? `
                <div id="group-${safeId}" class="combined-section-header accountingGroupClassification_accountingGroup_container">
                  <div class="sums-of-transactions section-header-ag-label">${classificationLabel}</div>
                  <div class="sums-of-transactions section-header-title section-header-underline">${combinedSectionLabel}</div>
                </div>`
                    : `
                <div id="group-${safeId}" class="combined-section-header combined-section-header-continued accountingGroupClassification_accountingGroup_container">
                  <div class="sums-of-transactions section-header-title section-header-underline">${combinedSectionLabel}</div>
                </div>`;


                // ${headerHtml}
                // <div style="margin-right: 40px;">
                //     <div class="sums-of-transactions" style="margin-bottom: 10px; text-decoration: underline;">${palCodeCategory}</div>
                // </div>

                content += `
                        ${generatePalCodeTables(transactionsForPalCodeCategory, palCodeCategory, headerHtml, printAccountingGroup, transactionsForPalCodeCategory[0]?.palCodeAccountGroup || '', secondPeriodData)}
                `;
            });
        });

        return content;
    };

    const generatePalCodeTables = (transactionsForPalCodeCategory, palCodeCategory, headerHtml, printAccountingGroup, accountingGroupName, secondPeriodData = null) => {
        let content = '';
        const groupedByPalCode = groupBy(transactionsForPalCodeCategory, 'palCode');
        const tableColSpan = getTableColSpan(secondPeriodData);

        let transactionsProcessed = 0;
        let headerDisplayedForCurrentPage = false;

        // Start the table once at the beginning
        content += `
        <div class="pageStyle">
            <div class="section-header-block">
                ${headerHtml}
            </div>
            <table class="table${getTableLayoutClass(secondPeriodData)}" dir="rtl">
                ${buildTableColGroup(secondPeriodData)}
    <thead>
        <tr class="tr">
            ${viewAccountCard ? `<th class="td-header-1">כרטיס חשבון</th>` : '<th class="td-header-1"></th>'}
            ${renderTrialBalanceHeaderCells()}
            ${showPeriodComparison && secondPeriodData ? renderTrialBalanceHeaderCells() : ''}
        </tr>
    </thead>
    <tbody id="table-body">
        `;

        const palCodes = Object.keys(groupedByPalCode);
        palCodes.forEach((palCode, index) => {
            const transactionsForPalCode = groupedByPalCode[palCode];
            const visiblePalCodeTransactions = getTransactionsForDisplay(transactionsForPalCode);

            if (!showAccountCardsWithZeros && visiblePalCodeTransactions.length === 0) {
                return;
            }

            // Add a row to divide different palCodes
            if (viewPalcodes) {
                content += `
                <tr>
                    <td class="overflowed" colspan="${tableColSpan}">${palCode === 'NonPalCode'
                        ? t('NonPalCode')
                        : palCode}</td>
                </tr>`
            }

            ;

            if (viewAccountCard) {
                visiblePalCodeTransactions
                    .forEach(tr => {
                        const transactionDetail = encodeURIComponent(JSON.stringify(tr)); // Escape quotes for embedding JSON in HTML
                        const diff = tr.initDebitAccount - tr.initCreditAccount;

                        // Get comparison transaction for this account card if in comparison mode
                        const comparisonTr = showPeriodComparison && secondPeriodData ?
                            secondPeriodData.transaction.find(ctr =>
                                ctr.accountCardId === tr.accountCardId && ctr.palCode === tr.palCode
                            ) : null;

                        const comparisonDiff = comparisonTr ? comparisonTr.initDebitAccount - comparisonTr.initCreditAccount : 0;

                        content += `
                <tr class="hoverTr" onclick="window.parent.dispatchEvent(new CustomEvent('rowClick', { detail: '${transactionDetail}' }))" style="cursor:pointer">
                    <td class="td-account-cell">${formatAccountCardLabel(tr.accountCardId, tr.accountCardName)}</td>
                    ${renderTrialBalanceNumericCells({
                        openingDebit: diff > 0 ? diff : 0,
                        openingCredit: diff < 0 ? -diff : 0,
                        debit: tr.debitAccount,
                        credit: tr.creditAccount,
                        balance: displayBalance(tr),
                        fcDebit: getTransactionField(tr, 'foreignCurrencyDebit'),
                        fcCredit: getTransactionField(tr, 'foreignCurrencyCredit'),
                        fcBalance: getTransactionField(tr, 'foreignCurrencyBalance'),
                    })}
                    ${showPeriodComparison && secondPeriodData ? renderTrialBalanceNumericCells({
                        openingDebit: comparisonDiff > 0 ? comparisonDiff : 0,
                        openingCredit: comparisonDiff < 0 ? -comparisonDiff : 0,
                        debit: comparisonTr ? comparisonTr.debitAccount : 0,
                        credit: comparisonTr ? comparisonTr.creditAccount : 0,
                        balance: comparisonTr ? displayBalance(comparisonTr) : 0,
                        fcDebit: comparisonTr ? getTransactionField(comparisonTr, 'foreignCurrencyDebit') : null,
                        fcCredit: comparisonTr ? getTransactionField(comparisonTr, 'foreignCurrencyCredit') : null,
                        fcBalance: comparisonTr ? getTransactionField(comparisonTr, 'foreignCurrencyBalance') : null,
                    }) : ''}
                </tr>
            `;

                        transactionsProcessed++;

                        // Check if we need to start a new page

                    });
            }


            // Add totals for the current PalCode
            content += generatePalCodeTotals(transactionsForPalCode, palCode, secondPeriodData);

            // If this is the last palCode in the current palCodeCategory, add the category totals
            if (index === palCodes.length - 1) {
                content += generatePalCodeCategoryTotals(transactionsForPalCodeCategory, palCodeCategory, secondPeriodData);


                if (printAccountingGroup) {
                    // 
                    const diff = totalInitDebit - totalInitCredit;
                    content += `
                    <tr class="tr-final-row-palcode-category pal-code-account-group-total-row">
                        <td class="total-palcode">סה''כ:  ${accountingGroupName === 'NonPalCodeAccountGroup'
                            ? t('NonPalCodeAccountGroup')
                            : accountingGroupName}</td>
                        ${renderTrialBalanceNumericCells({
                            openingDebit: diff > 0 ? diff : 0,
                            openingCredit: diff < 0 ? -diff : 0,
                            debit: totalDebit,
                            credit: totalCredit,
                            balance: profitAndLossDisplaySign(accountingGroupName) * totalBalance1,
                            fcDebit: showFc ? totalFcDebit : null,
                            fcCredit: showFc ? totalFcCredit : null,
                            fcBalance: showFc ? profitAndLossDisplaySign(accountingGroupName) * totalFcBalance : null,
                        }, { sum: true, topLine: true })}
                    </tr>`;
                    if (profitAndLossNames.includes(accountingGroupName)) {
                        profitAndLossTotalInitDebit += totalInitDebit;
                        profitAndLossTotalInitCredit += totalInitCredit;
                        profitAndLossTotalDebit += totalDebit;
                        profitAndLossTotalCredit += totalCredit;
                        profitAndLossTotalBalance1 += totalBalance1;
                        profitAndLossTotalFcDebit += totalFcDebit;
                        profitAndLossTotalFcCredit += totalFcCredit;
                        profitAndLossTotalFcBalance += totalFcBalance;
                    }
                    totalInitDebit = 0;
                    totalInitCredit = 0;
                    totalDebit = 0;
                    totalCredit = 0;
                    totalBalance1 = 0
                    totalFcDebit = 0;
                    totalFcCredit = 0;
                    totalFcBalance = 0;
                }
            }
        });

        // Close the final table and pageStyle div
        content += `
                    </tbody>
                </table>
            </div>
                `;


        return content;
    };







    const generatePalCodeTotals = (transactions, palCode, secondPeriodData = null) => {
        if (viewPalcodes) {
            const visibleTransactions = getTransactionsForDisplay(transactions);
            if (visibleTransactions.length === 0) {
                return '';
            }
            const totalInitDebitAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'initDebitAccount')), 0);
            const totalInitCreditAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'initCreditAccount')), 0);
            const totalDebitAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'debitAccount')), 0);
            const totalCreditAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'creditAccount')), 0);
            const totalBalance = sumDisplayBalance(visibleTransactions);
            const diff = totalInitDebitAccount - totalInitCreditAccount;

            // Calculate comparison totals if in comparison mode
            let comparisonTotals = '';
            if (showPeriodComparison && secondPeriodData) {
                const comparisonTransactions = secondPeriodData.transaction.filter(ctr => ctr.palCode === palCode);
                const comparisonTotalInitDebitAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.initDebitAccount, 0);
                const comparisonTotalInitCreditAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.initCreditAccount, 0);
                const comparisonTotalDebitAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.debitAccount, 0);
                const comparisonTotalCreditAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.creditAccount, 0);
                const comparisonTotalBalance = sumDisplayBalance(comparisonTransactions);
                const comparisonDiff = comparisonTotalInitDebitAccount - comparisonTotalInitCreditAccount;

                comparisonTotals = renderTrialBalanceNumericCells({
                    openingDebit: comparisonDiff > 0 ? comparisonDiff : 0,
                    openingCredit: comparisonDiff < 0 ? -comparisonDiff : 0,
                    debit: comparisonTotalDebitAccount,
                    credit: comparisonTotalCreditAccount,
                    balance: comparisonTotalBalance,
                    fcDebit: showFc ? sumFcField(comparisonTransactions, 'foreignCurrencyDebit') : null,
                    fcCredit: showFc ? sumFcField(comparisonTransactions, 'foreignCurrencyCredit') : null,
                    fcBalance: showFc ? sumFcField(comparisonTransactions, 'foreignCurrencyBalance') : null,
                }, { sum: true });
            }

            return `
            <tr class="tr-final-row-palcode">
                 <td class="total-palcode">סה''כ: ${palCode === 'NonPalCode' ? t('NonPalCode') : palCode} </td>
                 ${renderTrialBalanceNumericCells({
                     openingDebit: diff > 0 ? diff : 0,
                     openingCredit: diff < 0 ? -diff : 0,
                     debit: totalDebitAccount,
                     credit: totalCreditAccount,
                     balance: totalBalance,
                     fcDebit: showFc ? sumFcField(visibleTransactions, 'foreignCurrencyDebit') : null,
                     fcCredit: showFc ? sumFcField(visibleTransactions, 'foreignCurrencyCredit') : null,
                     fcBalance: showFc ? sumFcField(visibleTransactions, 'foreignCurrencyBalance') : null,
                 }, { sum: true })}
                 ${comparisonTotals}
             </tr>
         `;
        } else {
            return "";
        }

    };

    const generatePalCodeCategoryTotals = (transactions, palCodeCategory, secondPeriodData = null) => {
        const visibleTransactions = getTransactionsForDisplay(transactions);
        if (visibleTransactions.length === 0) {
            return '';
        }

        const totalInitDebitAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'initDebitAccount')), 0);
        const totalInitCreditAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'initCreditAccount')), 0);
        const totalDebitAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'debitAccount')), 0);
        const totalCreditAccount = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'creditAccount')), 0);
        const totalBalance = visibleTransactions.reduce((acc, tr) => acc + transactionAmount(getTransactionField(tr, 'balance')), 0);
        // P&L-signed value for display only; the raw totalBalance keeps feeding the
        // running accumulators so the overall trial-balance grand total still reconciles.
        const displayTotalBalance = sumDisplayBalance(visibleTransactions);

        if (visibleTransactions[0].palCodeAccountGroup === 'רכוש קבוע') {


        }


        totalInitDebit += totalInitDebitAccount;
        totalInitCredit += totalInitCreditAccount;
        totalDebit += totalDebitAccount;
        totalCredit += totalCreditAccount;
        totalBalance1 += totalBalance;
        // Accumulate to report totals
        totalInitDebitAccountReport += totalInitDebitAccount;
        totalInitCreditAccountReport += totalInitCreditAccount;
        totalDebitAccountReport += totalDebitAccount;
        totalCreditAccountReport += totalCreditAccount;
        totalBalanceReport += totalBalance;
        // Foreign-currency accumulation (group + report level).
        if (showFc) {
            const fcDebitAccount = sumFcField(visibleTransactions, 'foreignCurrencyDebit');
            const fcCreditAccount = sumFcField(visibleTransactions, 'foreignCurrencyCredit');
            const fcBalanceAccount = sumFcField(visibleTransactions, 'foreignCurrencyBalance');
            totalFcDebit += fcDebitAccount;
            totalFcCredit += fcCreditAccount;
            totalFcBalance += fcBalanceAccount;
            totalFcDebitReport += fcDebitAccount;
            totalFcCreditReport += fcCreditAccount;
            totalFcBalanceReport += fcBalanceAccount;
        }
        const diff = totalInitDebitAccount - totalInitCreditAccount;

        // Calculate comparison totals if in comparison mode
        let comparisonTotals = '';
        if (showPeriodComparison && secondPeriodData) {
            const comparisonTransactions = secondPeriodData.transaction.filter(ctr =>
                visibleTransactions.some(tr => tr.palCode === ctr.palCode && tr.accountCardId === ctr.accountCardId)
            );
            const comparisonTotalInitDebitAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.initDebitAccount, 0);
            const comparisonTotalInitCreditAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.initCreditAccount, 0);
            const comparisonTotalDebitAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.debitAccount, 0);
            const comparisonTotalCreditAccount = comparisonTransactions.reduce((acc, tr) => acc + tr.creditAccount, 0);
            const comparisonTotalBalance = sumDisplayBalance(comparisonTransactions);
            const comparisonDiff = comparisonTotalInitDebitAccount - comparisonTotalInitCreditAccount;

            comparisonTotals = renderTrialBalanceNumericCells({
                openingDebit: comparisonDiff > 0 ? comparisonDiff : 0,
                openingCredit: comparisonDiff < 0 ? -comparisonDiff : 0,
                debit: comparisonTotalDebitAccount,
                credit: comparisonTotalCreditAccount,
                balance: comparisonTotalBalance,
                fcDebit: showFc ? sumFcField(comparisonTransactions, 'foreignCurrencyDebit') : null,
                fcCredit: showFc ? sumFcField(comparisonTransactions, 'foreignCurrencyCredit') : null,
                fcBalance: showFc ? sumFcField(comparisonTransactions, 'foreignCurrencyBalance') : null,
            }, { sum: true });
        }

        return `
            <tr class="tr-final-row-palcode-category">
                <td class="total-palcode">סה''כ: ${palCodeCategory === 'NonPalCodeCategory'
                ? t('NonPalCodeCategory')
                : palCodeCategory}</td>
                ${renderTrialBalanceNumericCells({
                    openingDebit: diff > 0 ? diff : 0,
                    openingCredit: diff < 0 ? -diff : 0,
                    debit: totalDebitAccount,
                    credit: totalCreditAccount,
                    balance: displayTotalBalance,
                    fcDebit: showFc ? sumFcField(visibleTransactions, 'foreignCurrencyDebit') : null,
                    fcCredit: showFc ? sumFcField(visibleTransactions, 'foreignCurrencyCredit') : null,
                    fcBalance: showFc ? sumFcField(visibleTransactions, 'foreignCurrencyBalance') : null,
                }, { sum: true })}
                ${comparisonTotals}
            </tr>
        `;
    };

    const handleDownloadExcel = async () => {
        if (!data || !data.transaction) return;

        /* ---- Pick period data exactly like generateReportHtml ---- */
        let firstPeriodData;
        let secondPeriodData;
        if (showPeriodComparison && periodComparisonData?.data1 && periodComparisonData?.data2) {
            firstPeriodData = periodComparisonData.data1;
            secondPeriodData = periodComparisonData.data2;
        } else {
            firstPeriodData = data;
            secondPeriodData = null;
        }

        /* ---- Filter transactions exactly like the report ---- */
        let filteredTransactions = filterResetCardTransactions(
            firstPeriodData.transaction,
            showAccountCardsWithZeros
        );
        if (typeof selectedCurrency === 'number') {
            filteredTransactions = filteredTransactions.filter((tr) =>
                transactionAmount(getTransactionField(tr, 'foreignCurrencyDebit')) !== 0 ||
                transactionAmount(getTransactionField(tr, 'foreignCurrencyCredit')) !== 0 ||
                transactionAmount(getTransactionField(tr, 'foreignCurrencyBalance')) !== 0
            );
        }
        if (!filteredTransactions || filteredTransactions.length === 0) return;

        const uniquePalCodeCategories = [...new Set(filteredTransactions
            .map((tr) => tr.palCodeCategory)
            .filter((category) => category !== 'NonPalCodeCategory'))];
        const uniquePalCodes = [...new Set(filteredTransactions
            .map((tr) => tr.palCode)
            .filter((code) => code !== 'NonPalCode'))];

        const hasComparison = !!(secondPeriodData && Array.isArray(secondPeriodData.transaction));

        /* ---- Column model (mirrors renderTrialBalanceHeaderCells) ---- */
        const fcNumericHeaders = detailedTransactions
            ? [`חובה ${fcLabel}`, `זכות ${fcLabel}`, `יתרה ${fcLabel}`]
            : [`יתרה ${fcLabel}`];
        const periodNumericHeaders = (() => {
            if (fcOnly) return fcNumericHeaders;
            const fcHeaders = showFc ? fcNumericHeaders : [];
            if (!detailedTransactions) return ['יתרה', ...fcHeaders];
            if (showOpeningColumns) return ['י. פ. חובה', 'י. פ. זכות', 'חובה', 'זכות', 'יתרה', ...fcHeaders];
            return ['חובה', 'זכות', 'יתרה', ...fcHeaders];
        })();
        const perPeriodCount = periodNumericHeaders.length;
        const accountHeaderLabel = viewAccountCard ? 'כרטיס חשבון' : '';
        const headerRowLabels = hasComparison
            ? [accountHeaderLabel, ...periodNumericHeaders, ...periodNumericHeaders]
            : [accountHeaderLabel, ...periodNumericHeaders];
        const colCount = headerRowLabels.length;

        // Produce the ordered numeric values for one period (mirrors renderTrialBalanceNumericCells).
        // FC cells for non-total rows may be null -> emitted as '' (empty) so they stay blank.
        const numericValues = ({
            openingDebit = 0, openingCredit = 0, debit = 0, credit = 0, balance = 0,
            fcDebit = null, fcCredit = null, fcBalance = null,
        }) => {
            const num = (v) => transactionAmount(v);
            const fcVal = (v) => (v === null || v === undefined ? '' : transactionAmount(v));
            if (fcOnly) {
                const folded = foldFcOpening(fcDebit, fcCredit, fcBalance);
                return detailedTransactions
                    ? [fcVal(folded.debit), fcVal(folded.credit), fcVal(folded.balance)]
                    : [fcVal(folded.balance)];
            }
            const fcCells = !showFc ? [] : (detailedTransactions
                ? [fcVal(fcDebit), fcVal(fcCredit), fcVal(fcBalance)]
                : [fcVal(fcBalance)]);
            if (!detailedTransactions) return [num(balance), ...fcCells];
            if (showOpeningColumns) {
                return [num(openingDebit), num(openingCredit), num(debit), num(credit), num(balance), ...fcCells];
            }
            return [num(debit + openingDebit), num(credit + openingCredit), num(balance), ...fcCells];
        };
        const blankPeriodValues = () => new Array(perPeriodCount).fill('');

        /* ---- Style palette (xlsx-js-style), modelled on the Account Card export ---- */
        const PALETTE = {
            primary: 'FF2F4BFF',
            primaryDark: 'FF1E308F',
            headerFont: 'FFFFFFFF',
            opening: 'FFEFF2FF',
            subFill: 'FFE3E9FF',
            totalFill: 'FFD7DFFF',
            groupFill: 'FFBECBFF',
            grandFill: 'FFA9BBFF',
            stripe: 'FFF6F8FF',
            text: 'FF1F2937',
            border: 'FFC3CBDB',
        };
        const thinBorder = { style: 'thin', color: { rgb: PALETTE.border } };
        const allThin = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
        const STYLES = {
            clientLine: { font: { bold: true, sz: 14, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right' } },
            reportTitle: { font: { bold: true, sz: 20, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center' } },
            dateLine: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
            metaLine: { font: { italic: true, sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
            sectionHeader: { font: { bold: true, sz: 15, color: { rgb: PALETTE.primaryDark } }, alignment: { horizontal: 'right', vertical: 'center' } },
            sectionTitle: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primary }, underline: true }, alignment: { horizontal: 'right', vertical: 'center' } },
            palCodeLabel: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.opening } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            header: { font: { bold: true, sz: 12, color: { rgb: PALETTE.headerFont } }, fill: { fgColor: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: allThin },
            dataText: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            dataNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            stripeText: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            stripeNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            subtotalText: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            subtotalNum: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            groupText: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            groupNum: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            grandText: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.grandFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
            grandNum: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.grandFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
        };
        const ROW_HEIGHT = {
            clientLine: 24, reportTitle: 32, dateLine: 20, metaLine: 18,
            sectionHeader: 24, sectionTitle: 22, palCodeLabel: 20, header: 26,
            data: 18, stripe: 18, subtotal: 22, group: 24, grand: 26, blank: 8,
        };
        const mergedKinds = ['clientLine', 'reportTitle', 'dateLine', 'metaLine', 'sectionHeader', 'sectionTitle', 'palCodeLabel'];
        const totalKinds = { subtotal: ['subtotalText', 'subtotalNum'], group: ['groupText', 'groupNum'], grand: ['grandText', 'grandNum'] };

        /* ---- Assemble sheet rows ---- */
        const aoa = [];
        const merges = [];
        const rowMeta = [];
        const blankRow = () => new Array(colCount).fill('');
        const pushMerged = (text, kind) => {
            const r = aoa.length;
            const row = blankRow();
            row[0] = text;
            aoa.push(row);
            rowMeta.push({ kind });
            merges.push({ s: { r, c: 0 }, e: { r, c: Math.max(colCount - 1, 0) } });
        };
        const pushRow = (label, firstValues, secondValues, kind) => {
            const row = [label, ...firstValues];
            if (hasComparison) row.push(...(secondValues && secondValues.length ? secondValues : blankPeriodValues()));
            while (row.length < colCount) row.push('');
            aoa.push(row);
            rowMeta.push({ kind });
        };
        const pushBlank = () => { aoa.push(blankRow()); rowMeta.push({ kind: 'blank' }); };

        /* ---- Title / meta block ---- */
        const caseLabel = getCaseNumberLabel(client?.caseType ?? data?.caseType);
        pushMerged(`${caseLabel}: ${data.clientCase}    ${data.clientName}`, 'clientLine');
        const reportTitle = (trialBalanceType === 'Categories' || trialBalanceType === 'PalCodes')
            ? 'טיוטת מאזן'
            : 'דוח מאזן בוחן יתרות';
        pushMerged(`${reportTitle}${showFc ? ` - ${fcLabel}` : ''}`, 'reportTitle');
        const dateRangeText = hasComparison
            ? `מחודש ${firstPeriodData.startDate} עד חודש ${firstPeriodData.endDate}   |   מחודש ${secondPeriodData.startDate} עד חודש ${secondPeriodData.endDate}`
            : `מחודש ${firstPeriodData.startDate} עד חודש ${firstPeriodData.endDate}`;
        pushMerged(dateRangeText, 'dateLine');
        pushMerged(`מסעיף: ${uniquePalCodeCategories[0] || ''} - עד סעיף: ${uniquePalCodeCategories[uniquePalCodeCategories.length - 1] || ''}`, 'metaLine');
        pushMerged(`מסיווג: ${uniquePalCodes[0] || ''} - עד סיווג: ${uniquePalCodes[uniquePalCodes.length - 1] || ''}`, 'metaLine');
        pushBlank();

        /* ---- Local accumulators (mirror the component's module-level lets) ---- */
        // group-level (reset at each accounting-group boundary)
        let gInitDebit = 0, gInitCredit = 0, gDebit = 0, gCredit = 0, gBalance1 = 0;
        let gFcDebit = 0, gFcCredit = 0, gFcBalance = 0;
        // report-level
        let rInitDebit = 0, rInitCredit = 0, rDebit = 0, rCredit = 0, rBalance = 0;
        let rFcDebit = 0, rFcCredit = 0, rFcBalance = 0;
        // profit & loss level
        let plInitDebit = 0, plInitCredit = 0, plDebit = 0, plCredit = 0, plBalance1 = 0;
        let plFcDebit = 0, plFcCredit = 0, plFcBalance = 0;

        /* ---- Comparison helpers (mirror the HTML second-period cells) ---- */
        const comparisonRowValues = (tr) => {
            if (!hasComparison) return [];
            const ctr = secondPeriodData.transaction.find((c) =>
                c.accountCardId === tr.accountCardId && c.palCode === tr.palCode) || null;
            const cDiff = ctr
                ? transactionAmount(getTransactionField(ctr, 'initDebitAccount')) - transactionAmount(getTransactionField(ctr, 'initCreditAccount'))
                : 0;
            return numericValues({
                openingDebit: cDiff > 0 ? cDiff : 0,
                openingCredit: cDiff < 0 ? -cDiff : 0,
                debit: ctr ? getTransactionField(ctr, 'debitAccount') : 0,
                credit: ctr ? getTransactionField(ctr, 'creditAccount') : 0,
                balance: ctr ? displayBalance(ctr) : 0,
                fcDebit: ctr ? getTransactionField(ctr, 'foreignCurrencyDebit') : null,
                fcCredit: ctr ? getTransactionField(ctr, 'foreignCurrencyCredit') : null,
                fcBalance: ctr ? getTransactionField(ctr, 'foreignCurrencyBalance') : null,
            });
        };
        const comparisonPalCodeValues = (palCode) => {
            if (!hasComparison) return [];
            const cts = secondPeriodData.transaction.filter((c) => c.palCode === palCode);
            const cd = cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'initDebitAccount')), 0);
            const cc = cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'initCreditAccount')), 0);
            const cDiff = cd - cc;
            return numericValues({
                openingDebit: cDiff > 0 ? cDiff : 0,
                openingCredit: cDiff < 0 ? -cDiff : 0,
                debit: cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'debitAccount')), 0),
                credit: cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'creditAccount')), 0),
                balance: sumDisplayBalance(cts),
                fcDebit: showFc ? sumFcField(cts, 'foreignCurrencyDebit') : null,
                fcCredit: showFc ? sumFcField(cts, 'foreignCurrencyCredit') : null,
                fcBalance: showFc ? sumFcField(cts, 'foreignCurrencyBalance') : null,
            });
        };
        const comparisonCategoryValues = (visibleCategory) => {
            if (!hasComparison) return [];
            const cts = secondPeriodData.transaction.filter((c) =>
                visibleCategory.some((tr) => tr.palCode === c.palCode && tr.accountCardId === c.accountCardId));
            const cd = cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'initDebitAccount')), 0);
            const cc = cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'initCreditAccount')), 0);
            const cDiff = cd - cc;
            return numericValues({
                openingDebit: cDiff > 0 ? cDiff : 0,
                openingCredit: cDiff < 0 ? -cDiff : 0,
                debit: cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'debitAccount')), 0),
                credit: cts.reduce((a, c) => a + transactionAmount(getTransactionField(c, 'creditAccount')), 0),
                balance: sumDisplayBalance(cts),
                fcDebit: showFc ? sumFcField(cts, 'foreignCurrencyDebit') : null,
                fcCredit: showFc ? sumFcField(cts, 'foreignCurrencyCredit') : null,
                fcBalance: showFc ? sumFcField(cts, 'foreignCurrencyBalance') : null,
            });
        };

        /* ---- Build the grouped body (mirror generateAccountingGroupClassificationSections/generatePalCodeTables) ---- */
        const groupedByClassification = groupBy(filteredTransactions, 'accountingGroupClassification');
        Object.keys(groupedByClassification).forEach((accountingGroupClassification) => {
            const txForClassification = groupedByClassification[accountingGroupClassification];
            const groupedByCategory = groupBy(txForClassification, 'palCodeCategory');
            const palCodeAccountGroup = txForClassification[0]?.palCodeAccountGroup || '';
            const categories = Object.keys(groupedByCategory);

            categories.forEach((palCodeCategory, index) => {
                const currentCategory = groupedByCategory[palCodeCategory];
                const nextCategory = groupedByCategory[categories[index + 1]];
                const printAccountingGroup = !nextCategory
                    || (currentCategory[0]?.palCodeAccountGroup !== nextCategory[0]?.palCodeAccountGroup);
                const transactionsForPalCodeCategory = currentCategory;
                const accountingGroupName = transactionsForPalCodeCategory[0]?.palCodeAccountGroup || '';

                const categoryLabel = palCodeCategory === 'NonPalCodeCategory'
                    ? t('NonPalCodeCategory')
                    : palCodeCategory;
                const groupLabelRaw = transactionsForPalCodeCategory[0]?.palCodeAccountGroup || palCodeAccountGroup;
                const groupLabel = groupLabelRaw === 'NonPalCodeAccountGroup'
                    ? t('NonPalCodeAccountGroup')
                    : groupLabelRaw;
                const combinedSectionLabel = groupLabel ? `${categoryLabel} - ${groupLabel}` : categoryLabel;
                const classificationLabel = accountingGroupClassification === 'NonAccountingGroupClassifaction'
                    ? t('NonAccountingGroupClassifaction')
                    : accountingGroupClassification;

                pushBlank();
                if (index === 0) {
                    pushMerged(classificationLabel, 'sectionHeader');
                }
                pushMerged(combinedSectionLabel, 'sectionTitle');

                // Column header (blue bar) for this section table.
                pushRow(accountHeaderLabel, periodNumericHeaders, hasComparison ? periodNumericHeaders : [], 'header');

                const groupedByPalCode = groupBy(transactionsForPalCodeCategory, 'palCode');
                const palCodes = Object.keys(groupedByPalCode);

                palCodes.forEach((palCode, pIndex) => {
                    const transactionsForPalCode = groupedByPalCode[palCode];
                    const visiblePalCode = getTransactionsForDisplay(transactionsForPalCode);
                    if (!showAccountCardsWithZeros && visiblePalCode.length === 0) return;

                    if (viewPalcodes) {
                        pushMerged(palCode === 'NonPalCode' ? t('NonPalCode') : palCode, 'palCodeLabel');
                    }

                    if (viewAccountCard) {
                        visiblePalCode.forEach((tr, idx) => {
                            const diff = transactionAmount(getTransactionField(tr, 'initDebitAccount'))
                                - transactionAmount(getTransactionField(tr, 'initCreditAccount'));
                            const values = numericValues({
                                openingDebit: diff > 0 ? diff : 0,
                                openingCredit: diff < 0 ? -diff : 0,
                                debit: getTransactionField(tr, 'debitAccount'),
                                credit: getTransactionField(tr, 'creditAccount'),
                                balance: displayBalance(tr),
                                fcDebit: getTransactionField(tr, 'foreignCurrencyDebit'),
                                fcCredit: getTransactionField(tr, 'foreignCurrencyCredit'),
                                fcBalance: getTransactionField(tr, 'foreignCurrencyBalance'),
                            });
                            pushRow(
                                formatAccountCardLabel(tr.accountCardId, tr.accountCardName),
                                values,
                                comparisonRowValues(tr),
                                idx % 2 === 1 ? 'stripe' : 'data'
                            );
                        });
                    }

                    // palCode subtotal (generatePalCodeTotals)
                    if (viewPalcodes && visiblePalCode.length > 0) {
                        const sInitDebit = visiblePalCode.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'initDebitAccount')), 0);
                        const sInitCredit = visiblePalCode.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'initCreditAccount')), 0);
                        const sDebit = visiblePalCode.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'debitAccount')), 0);
                        const sCredit = visiblePalCode.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'creditAccount')), 0);
                        const sBalance = sumDisplayBalance(visiblePalCode);
                        const sDiff = sInitDebit - sInitCredit;
                        const values = numericValues({
                            openingDebit: sDiff > 0 ? sDiff : 0,
                            openingCredit: sDiff < 0 ? -sDiff : 0,
                            debit: sDebit,
                            credit: sCredit,
                            balance: sBalance,
                            fcDebit: showFc ? sumFcField(visiblePalCode, 'foreignCurrencyDebit') : null,
                            fcCredit: showFc ? sumFcField(visiblePalCode, 'foreignCurrencyCredit') : null,
                            fcBalance: showFc ? sumFcField(visiblePalCode, 'foreignCurrencyBalance') : null,
                        });
                        pushRow(
                            `סה"כ: ${palCode === 'NonPalCode' ? t('NonPalCode') : palCode} `,
                            values,
                            comparisonPalCodeValues(palCode),
                            'subtotal'
                        );
                    }

                    // Category total + accounting-group total on the last palCode.
                    if (pIndex === palCodes.length - 1) {
                        const visibleCategory = getTransactionsForDisplay(transactionsForPalCodeCategory);
                        if (visibleCategory.length > 0) {
                            const cInitDebit = visibleCategory.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'initDebitAccount')), 0);
                            const cInitCredit = visibleCategory.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'initCreditAccount')), 0);
                            const cDebit = visibleCategory.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'debitAccount')), 0);
                            const cCredit = visibleCategory.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'creditAccount')), 0);
                            const cBalanceRaw = visibleCategory.reduce((a, tr) => a + transactionAmount(getTransactionField(tr, 'balance')), 0);
                            const cDisplayBalance = sumDisplayBalance(visibleCategory);

                            // Accumulate into group + report (side effect, mirrors generatePalCodeCategoryTotals).
                            gInitDebit += cInitDebit;
                            gInitCredit += cInitCredit;
                            gDebit += cDebit;
                            gCredit += cCredit;
                            gBalance1 += cBalanceRaw;
                            rInitDebit += cInitDebit;
                            rInitCredit += cInitCredit;
                            rDebit += cDebit;
                            rCredit += cCredit;
                            rBalance += cBalanceRaw;
                            let cFcDebit = null, cFcCredit = null, cFcBalance = null;
                            if (showFc) {
                                cFcDebit = sumFcField(visibleCategory, 'foreignCurrencyDebit');
                                cFcCredit = sumFcField(visibleCategory, 'foreignCurrencyCredit');
                                cFcBalance = sumFcField(visibleCategory, 'foreignCurrencyBalance');
                                gFcDebit += cFcDebit;
                                gFcCredit += cFcCredit;
                                gFcBalance += cFcBalance;
                                rFcDebit += cFcDebit;
                                rFcCredit += cFcCredit;
                                rFcBalance += cFcBalance;
                            }
                            const cDiff = cInitDebit - cInitCredit;
                            const values = numericValues({
                                openingDebit: cDiff > 0 ? cDiff : 0,
                                openingCredit: cDiff < 0 ? -cDiff : 0,
                                debit: cDebit,
                                credit: cCredit,
                                balance: cDisplayBalance,
                                fcDebit: cFcDebit,
                                fcCredit: cFcCredit,
                                fcBalance: cFcBalance,
                            });
                            pushRow(
                                `סה"כ: ${palCodeCategory === 'NonPalCodeCategory' ? t('NonPalCodeCategory') : palCodeCategory}`,
                                values,
                                comparisonCategoryValues(visibleCategory),
                                'subtotal'
                            );
                        }

                        if (printAccountingGroup) {
                            const gDiff = gInitDebit - gInitCredit;
                            const values = numericValues({
                                openingDebit: gDiff > 0 ? gDiff : 0,
                                openingCredit: gDiff < 0 ? -gDiff : 0,
                                debit: gDebit,
                                credit: gCredit,
                                balance: profitAndLossDisplaySign(accountingGroupName) * gBalance1,
                                fcDebit: showFc ? gFcDebit : null,
                                fcCredit: showFc ? gFcCredit : null,
                                fcBalance: showFc ? profitAndLossDisplaySign(accountingGroupName) * gFcBalance : null,
                            });
                            // Accounting-group total row (no second-period cells, matching the HTML).
                            pushRow(
                                `סה"כ:  ${accountingGroupName === 'NonPalCodeAccountGroup' ? t('NonPalCodeAccountGroup') : accountingGroupName}`,
                                values,
                                blankPeriodValues(),
                                'group'
                            );
                            if (profitAndLossNames.includes(accountingGroupName)) {
                                plInitDebit += gInitDebit;
                                plInitCredit += gInitCredit;
                                plDebit += gDebit;
                                plCredit += gCredit;
                                plBalance1 += gBalance1;
                                plFcDebit += gFcDebit;
                                plFcCredit += gFcCredit;
                                plFcBalance += gFcBalance;
                            }
                            gInitDebit = 0; gInitCredit = 0; gDebit = 0; gCredit = 0; gBalance1 = 0;
                            gFcDebit = 0; gFcCredit = 0; gFcBalance = 0;
                        }
                    }
                });
            });
        });

        /* ---- Final report totals (mirror generateReportHtml's finalSummaryRow) ---- */
        // Comparison final totals (mirror the second-period loop in generateReportHtml).
        let cmpPlValues = [];
        let cmpReportValues = [];
        if (hasComparison) {
            let cPlInitDebit = 0, cPlInitCredit = 0, cPlDebit = 0, cPlCredit = 0, cPlBalance = 0;
            let cRInitDebit = 0, cRInitCredit = 0, cRDebit = 0, cRCredit = 0, cRBalance = 0;
            let cRFcDebit = 0, cRFcCredit = 0, cRFcBalance = 0;
            let cPlFcDebit = 0, cPlFcCredit = 0, cPlFcBalance = 0;
            secondPeriodData.transaction.forEach((tr) => {
                cRInitDebit += transactionAmount(getTransactionField(tr, 'initDebitAccount'));
                cRInitCredit += transactionAmount(getTransactionField(tr, 'initCreditAccount'));
                cRDebit += transactionAmount(getTransactionField(tr, 'debitAccount'));
                cRCredit += transactionAmount(getTransactionField(tr, 'creditAccount'));
                cRBalance += transactionAmount(getTransactionField(tr, 'balance'));
                if (showFc) {
                    cRFcDebit += transactionAmount(getTransactionField(tr, 'foreignCurrencyDebit'));
                    cRFcCredit += transactionAmount(getTransactionField(tr, 'foreignCurrencyCredit'));
                    cRFcBalance += transactionAmount(getTransactionField(tr, 'foreignCurrencyBalance'));
                }
                if (profitAndLossNames.includes(tr.palCodeAccountGroup)) {
                    cPlInitDebit += transactionAmount(getTransactionField(tr, 'initDebitAccount'));
                    cPlInitCredit += transactionAmount(getTransactionField(tr, 'initCreditAccount'));
                    cPlDebit += transactionAmount(getTransactionField(tr, 'debitAccount'));
                    cPlCredit += transactionAmount(getTransactionField(tr, 'creditAccount'));
                    cPlBalance += transactionAmount(getTransactionField(tr, 'balance'));
                    if (showFc) {
                        cPlFcDebit += transactionAmount(getTransactionField(tr, 'foreignCurrencyDebit'));
                        cPlFcCredit += transactionAmount(getTransactionField(tr, 'foreignCurrencyCredit'));
                        cPlFcBalance += transactionAmount(getTransactionField(tr, 'foreignCurrencyBalance'));
                    }
                }
            });
            const cPlDiff = cPlInitDebit - cPlInitCredit;
            cmpPlValues = numericValues({
                openingDebit: cPlDiff > 0 ? cPlDiff : 0,
                openingCredit: cPlDiff < 0 ? -cPlDiff : 0,
                debit: cPlDebit,
                credit: cPlCredit,
                balance: cPlBalance,
                fcDebit: showFc ? cPlFcDebit : null,
                fcCredit: showFc ? cPlFcCredit : null,
                fcBalance: showFc ? cPlFcBalance : null,
            });
            const cRDiff = cRInitDebit - cRInitCredit;
            cmpReportValues = numericValues({
                openingDebit: cRDiff > 0 ? cRDiff : 0,
                openingCredit: cRDiff < 0 ? -cRDiff : 0,
                debit: cRDebit,
                credit: cRCredit,
                balance: cRBalance,
                fcDebit: showFc ? cRFcDebit : null,
                fcCredit: showFc ? cRFcCredit : null,
                fcBalance: showFc ? cRFcBalance : null,
            });
        }

        const plDiff = plInitDebit - plInitCredit;
        pushRow(
            'סה"כ רווח והפסד בדוח:',
            numericValues({
                openingDebit: plDiff > 0 ? plDiff : 0,
                openingCredit: plDiff < 0 ? -plDiff : 0,
                debit: plDebit,
                credit: plCredit,
                balance: plBalance1,
                fcDebit: showFc ? plFcDebit : null,
                fcCredit: showFc ? plFcCredit : null,
                fcBalance: showFc ? plFcBalance : null,
            }),
            cmpPlValues,
            'grand'
        );
        const rDiff = rInitDebit - rInitCredit;
        pushRow(
            'סה"כ בדוח:',
            numericValues({
                openingDebit: rDiff > 0 ? rDiff : 0,
                openingCredit: rDiff < 0 ? -rDiff : 0,
                debit: rDebit,
                credit: rCredit,
                balance: rBalance,
                fcDebit: showFc ? rFcDebit : null,
                fcCredit: showFc ? rFcCredit : null,
                fcBalance: showFc ? rFcBalance : null,
            }),
            cmpReportValues,
            'grand'
        );

        /* ---- Create worksheet ---- */
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!merges'] = merges.filter(Boolean);
        ws['!cols'] = Array.from({ length: colCount }, (_, c) => ({ wch: c === 0 ? 34 : 18 }));
        ws['!rows'] = rowMeta.map((m) => ({ hpt: ROW_HEIGHT[m.kind] || 18 }));
        ws['!views'] = [{ RTL: true }];

        aoa.forEach((row, r) => {
            const meta = rowMeta[r];
            if (!meta || meta.kind === 'blank') return;
            if (mergedKinds.includes(meta.kind)) {
                const addr = XLSX.utils.encode_cell({ r, c: 0 });
                if (ws[addr]) ws[addr].s = STYLES[meta.kind];
                return;
            }
            for (let c = 0; c < colCount; c += 1) {
                const addr = XLSX.utils.encode_cell({ r, c });
                let cell = ws[addr];
                if (!cell) { cell = { t: 's', v: '' }; ws[addr] = cell; }
                const isNum = c >= 1;
                if (meta.kind === 'header') {
                    cell.s = STYLES.header;
                } else if (totalKinds[meta.kind]) {
                    const [textStyle, numStyle] = totalKinds[meta.kind];
                    cell.s = isNum ? STYLES[numStyle] : STYLES[textStyle];
                } else if (meta.kind === 'stripe') {
                    cell.s = isNum ? STYLES.stripeNum : STYLES.stripeText;
                } else {
                    cell.s = isNum ? STYLES.dataNum : STYLES.dataText;
                }
                if (isNum && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
            }
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
        applyWorkbookRtl(wb);
        try {
            await translateWb(wb);
        } catch {
            return;
        }
        XLSX.writeFile(wb, `${data.clientName}_TransactionReport.xlsx`);
    };


    const handleDownloadPdf = async () => {
        const pageElements = document.querySelectorAll('.pageStyle');
        try {
            // let pdfContent = parseAndInsertPageBreaksNoDom(reportHtmlContent, data, showWithZeros);

            let pdfContent = reportHtmlContent;

            // Remove the border and box-shadow styles directly
            pdfContent = pdfContent.replace(/border: 1px solid #ccc;/g, '');
            pdfContent = pdfContent.replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0.1\);/g, '');
            pdfContent = await translateHtml(pdfContent);

            const response = await EnumsService.GeneratePdfFromHtml(pdfContent, data.clientCase, token);
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                // Set the filename using clientName and clientCase
                const fileName = `${data.clientName}-${data.clientCase}_דוח מאזן בוחן.pdf`;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                console.error('Failed to convert HTML to PDF:', response.status);
            }
        } catch (error) {
            console.error('Error converting HTML to PDF:', error);
        }
    };

    const handlePrint = async () => {
        const printWindow = window.open('', '_blank');
        let html;
        try {
            html = await translateHtml(reportHtmlContent);
        } catch {
            printWindow.close();
            return;
        }
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
    };

    // Chat functionality - Ask if user wants to add signature
    const handleSendToChatClick = async () => {
        const result = await Swal.fire({
            title: t('AddSignatureTitle') || 'Add Signature?',
            text: t('AddSignatureQuestion') || 'Do you want to add a digital signature to this document before sending?',
            icon: 'question',
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonColor: '#667eea',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('YesAddSignature') || 'Yes, Add Signature',
            cancelButtonText: t('NoSendWithout') || 'No, Send Without Signature',
            reverseButtons: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
            didOpen: () => {
                // Move close button to the left
                const closeButton = document.querySelector('.swal2-close');
                if (closeButton) {
                    closeButton.style.setProperty('right', 'auto', 'important');
                    closeButton.style.setProperty('left', '10px', 'important');
                }
            }
        });

        if (result.isConfirmed) {
            // User wants to add signature - open signature modal
            setShowSignatureModal(true);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
            // User wants to send without signature
            await handleSendToChat();
        }
        // If user closes by clicking outside, ESC, or X button - do nothing
    };

    // Helper function to generate HTML with signature (synchronous)
    const generateHtmlWithSignature = (signatureDataUrl) => {
        if (!data) return "";

        // Create signature section HTML with RTL direction
        // In RTL: First div = RIGHT, Second div = LEFT
        const signatureSection = `
<div class="signature-section" style="margin-top: 40px; padding: 20px; page-break-inside: avoid; direction: rtl;">
  <div style="border-top: 2px solid #333; padding-top: 20px;">
    <div style="display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 20px; direction: rtl;">
      <div style="flex: 1; min-width: 200px;">
        <div style="text-align: center; margin-bottom: 10px;">
          <img src="${signatureDataUrl}" alt="Signature" style="max-width: 300px; max-height: 100px; border: 1px solid #e0e0e0; padding: 5px; background: white;" />
        </div>
        <div style="border-top: 2px solid #000; padding-top: 5px; text-align: center;">
          <strong>חתימה מורשית</strong>
        </div>
      </div>
      <div style="flex: 1; min-width: 200px; text-align: center;">
        <div style="margin-bottom: 10px;">
          ${new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        <div style="border-top: 2px solid #000; padding-top: 5px;">
          <strong>תאריך</strong>
        </div>
      </div>
    </div>
  </div>
</div>`;

        // Get the current HTML content without signature
        let htmlWithoutSignature = reportHtmlContent;
        
        // Remove the border and box-shadow styles
        htmlWithoutSignature = htmlWithoutSignature.replace(/border: 1px solid #ccc;/g, '');
        htmlWithoutSignature = htmlWithoutSignature.replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0.1\);/g, '');
        
        // Insert signature before closing body tag
        let htmlWithSignature = htmlWithoutSignature;
        
        // Try multiple patterns to ensure we find the right place
        if (htmlWithSignature.includes('</div>\n            </body>')) {
            htmlWithSignature = htmlWithSignature.replace(
                '</div>\n            </body>',
                `${signatureSection}\n</div>\n            </body>`
            );
        } else if (htmlWithSignature.includes('</div></body>')) {
            htmlWithSignature = htmlWithSignature.replace(
                '</div></body>',
                `${signatureSection}\n</div></body>`
            );
        } else {
            htmlWithSignature = htmlWithSignature.replace(
                '</body>',
                `${signatureSection}\n</body>`
            );
        }

        return htmlWithSignature;
    };

    // Handle sending with signature after user signs
    const handleSendWithSignature = async (signature) => {
        try {
            setShowSignatureModal(false);

            // Show loading toast
            const loadingToast = toast.info(t('GeneratingSignedPDF') || 'Generating signed PDF...', {
                autoClose: false,
                closeButton: false
            });

            try {
                // First, open chat with the client
                await selectUserAndOpenChat(client.caseId, client.name || 'Client');

                // Generate HTML with signature embedded SYNCHRONOUSLY
                const htmlWithSignature = await translateHtml(generateHtmlWithSignature(signature));

                // Generate PDF content with signature embedded
                const res = await EnumsService.GeneratePdfFromHtml(htmlWithSignature, data.clientCase, token);
                if (!res.ok) {
                    console.error("PDF generation error", res.status);
                    const errorText = await res.text();
                    console.error("Error details:", errorText);
                    toast.dismiss(loadingToast);
                    toast.error(t('ErrorGeneratingPDF'));
                    return;
                }

                // Check response content type
                const contentType = res.headers.get('content-type');
                let blob;

                // Check if response is JSON or direct blob
                if (contentType && contentType.includes('application/json')) {
                    // Response is JSON with base64
                    const responseData = await res.json();

                    const pdfBase64 = responseData.data?.PdfBase64 || responseData.data?.pdfBase64;

                    if (!pdfBase64) {
                        toast.dismiss(loadingToast);
                        toast.error(t('ErrorGeneratingPDF'));
                        return;
                    }

                    // Convert base64 to blob
                    const binaryString = atob(pdfBase64);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    blob = new Blob([bytes], { type: 'application/pdf' });
                } else {
                    // Response is direct blob (PDF)
                    blob = await res.blob();
                }

                const file = new File([blob], `${data.clientName}-${data.clientCase}_דוח מאזן בוחן.pdf`, { type: 'application/pdf' });

                // Send the file to chat
                await handleSendMessageToChat(file, client.caseId, client.name);

                toast.dismiss(loadingToast);
                toast.success(t('DocumentSentToChat'));

            } catch (innerError) {
                console.error('Error in PDF generation/sending:', innerError);
                toast.dismiss(loadingToast);
                toast.error(t('ErrorSendingToChat'));
            }

        } catch (error) {
            console.error('Error sending document to chat:', error);
            toast.error(t('ErrorSendingToChat'));
        }
    };

    // Send without signature (original function)
    const handleSendToChat = async () => {
        try {
            // Show loading toast
            const loadingToast = toast.info(t('GeneratingPDF') || 'Generating PDF...', {
                autoClose: false,
                closeButton: false
            });

            // First, open chat with the client
            await selectUserAndOpenChat(client.caseId, client.name || 'Client');

            // Generate PDF content (similar to handleDownloadPdf)
            let pdfContent = reportHtmlContent;

            // Remove the border and box-shadow styles directly
            pdfContent = pdfContent.replace(/border: 1px solid #ccc;/g, '');
            pdfContent = pdfContent.replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0.1\);/g, '');
            pdfContent = await translateHtml(pdfContent);

            // Generate PDF using the same service as handleDownloadPdf
            const response = await EnumsService.GeneratePdfFromHtml(pdfContent, data.clientCase, token);

            if (response.ok) {
                // Check response content type
                const contentType = response.headers.get('content-type');
                let blob;

                if (contentType && contentType.includes('application/json')) {
                    // Response is JSON with base64
                    const responseData = await response.json();
                    const pdfBase64 = responseData.data?.PdfBase64 || responseData.data?.pdfBase64;

                    if (!pdfBase64) {
                        toast.dismiss(loadingToast);
                        toast.error(t('ErrorGeneratingPDF'));
                        return;
                    }

                    // Convert base64 to blob
                    const binaryString = atob(pdfBase64);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    blob = new Blob([bytes], { type: 'application/pdf' });
                } else {
                    // Response is direct blob (PDF)
                    blob = await response.blob();
                }

                const file = new File([blob], `${data.clientName}-${data.clientCase}_דוח מאזן בוחן.pdf`, { type: 'application/pdf' });

                // Send the file to chat
                await handleSendMessageToChat(file, client.caseId, client.name);
                
                toast.dismiss(loadingToast);
                toast.success(t('FileSentToChat'));
            } else {
                console.error('Failed to generate PDF for chat:', response.status);
                toast.dismiss(loadingToast);
                toast.error(t('ErrorGeneratingPDFForChat'));
            }
        } catch (error) {
            console.error('Error sending report to chat:', error);
            toast.error(t('ErrorSendingToChat'));
        }
    };

    useEffect(() => {
        let filteredTransactions = filterResetCardTransactions(
            data.transaction,
            showAccountCardsWithZeros
        );

        const allCategories = [...new Set(filteredTransactions
            .map(tr => tr.palCodeCategory)
            .filter(category => category !== "NonPalCodeCategory"))];


        allCategories.forEach((category) => {
            // Build the same ID you used above
            const safeId = category;
            const el = document.getElementById(`group-${safeId}`);
            if (el) {
                console.log(`Category "${category}" found at offsetTop=`, el.offsetTop);
            }
        });
    }, [reportHtmlContent, data.transaction]);

    // const parseAndInsertPageBreaksNoDom = (reportHtml, data, showWithZeros) => {
    //     // 1) Filter transactions if showWithZeros is false
    //     let filteredTransactions = data.transaction;
    //     if (!showWithZeros) {
    //         filteredTransactions = data.transaction.filter(
    //             (tr) => tr.initCreditAccount !== 0 || tr.initDebitAccount !== 0
    //         );
    //     }

    //     // 2) Gather all categories except "NonPalCodeCategory"
    //     const allCategories = [
    //         ...new Set(
    //             filteredTransactions
    //                 .map((tr) => tr.palCodeCategory)
    //                 .filter((cat) => cat !== 'NonPalCodeCategory')
    //         ),
    //     ];

    //     // We'll modify this string in-place
    //     let updatedHtml = reportHtml;
    //     let insertionOffset = 0;

    //     // Our page-break snippet



    //     // 3) For each category, find the container's opening & closing
    //     allCategories.forEach((category) => {
    //         const searchPattern = `id="group-${category}"`;

    //         // Find the container's <div id="group-<category>"> starting from insertionOffset
    //         const startIndex = updatedHtml.indexOf(searchPattern, insertionOffset);

    //         if (startIndex !== -1) {
    //             // Next, find this container's closing `</div>` 
    //             // (naively: the FIRST occurrence of </div> after startIndex)
    //             const closeDivIndex = updatedHtml.indexOf('</div>', startIndex);
    //             if (closeDivIndex !== -1) {
    //                 // Insert a page-break after `</div>` => which is 6 chars long
    //                 const insertPos = closeDivIndex + 6; // position right after `</div>`
    //                 updatedHtml =
    //                     updatedHtml.slice(0, insertPos) +
    //                     pageBreakDiv +
    //                     updatedHtml.slice(insertPos);

    //                 // Advance insertionOffset beyond the newly inserted snippet
    //                 insertionOffset = insertPos + pageBreakDiv.length;
    //             }
    //         }
    //     });

    //     return updatedHtml;
    // };

    return (
        <>
            <Modal open={open} onClose={handleClose} aria-labelledby="account-card-report-modal-title" aria-describedby="account-card-report-modal-description">
                <Box sx={modalStyle} >


                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }} className={`${direction}`}>
                        <div
                            className="pdf-view-upper"
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginBottom: '15px',
                                flexWrap: 'wrap',
                                gap: '8px',
                                padding: '8px',
                            }}
                        >
                            <SleekButton
                                onClick={handleDownloadPdf}
                                startIcon={<DownloadIcon />}
                            >
                                {t('DownloadPDFFile')}
                            </SleekButton>

                            <SleekButton
                                onClick={handleDownloadExcel}
                                startIcon={<TableChartIcon />}
                            >
                                {t('DownloadExcelFile')}
                            </SleekButton>

                            <SleekButton
                                onClick={handlePrint}
                                startIcon={<PrintIcon />}
                            >
                                {t('PrintReport')}
                            </SleekButton>

                            <SleekButton
                                onClick={handleSendToChatClick}
                                startIcon={<ChatIcon />}
                            >
                                {t('SendToChat')}
                            </SleekButton>

                            <SleekButton
                                onClick={handleOpenMonthlyReportModal}
                                startIcon={<CalendarMonthIcon />}
                            >
                                {t('MonthlyReport')}
                            </SleekButton>

                            {!showPeriodComparison ? (
                                <SleekButton
                                    onClick={handleOpenPeriodSelection}
                                    startIcon={<CompareIcon />}
                                >
                                    {t('ComparePeriods')}
                                </SleekButton>
                            ) : (
                                <SleekButton
                                    onClick={handleCloseComparisonWithRebuild}
                                    sx={{
                                        background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #E55A2B 0%, #D44A1A 100%)',
                                        }
                                    }}
                                >
                                    {t('CloseComparison')}
                                </SleekButton>
                            )}

                            <ReportCurrencySelector
                                selectedCurrency={selectedCurrency}
                                availableCurrencies={availableCurrencies}
                                onCurrencyChange={onCurrencyChange}
                            />

                            <ReportLanguageSelector
                                reportLanguage={reportLanguage}
                                onReportLanguageChange={setReportLanguage}
                                isTranslating={isTranslating}
                            />

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: '#f8f9ff',
                                borderRadius: '8px',
                                border: '1px solid #e3e8ff',
                                height: '36px',
                            }}>
                                <Checkbox
                                    checked={showAccountCardsWithZeros}
                                    onChange={(event) => handleShow0CardsCheckbox(event.target.checked)}
                                    name="ShowResetCards"
                                    color="primary"
                                    sx={{ padding: '2px' }}
                                    size="small"
                                />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                                    {t('ShowResetCards')}
                                </span>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: '#f8f9ff',
                                borderRadius: '8px',
                                border: '1px solid #e3e8ff',
                                height: '36px',
                            }}>
                                <Checkbox
                                    checked={detailedTransactions}
                                    onChange={(event) => handleShowDetailsTransactions(event.target.checked)}
                                    name="Detailed"
                                    color="primary"
                                    sx={{ padding: '2px' }}
                                    size="small"
                                />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                                    {t('Detailed')}
                                </span>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: '#f8f9ff',
                                borderRadius: '8px',
                                border: '1px solid #e3e8ff',
                                height: '36px',
                            }}>
                                <Checkbox
                                    checked={showAccountCodeId}
                                    onChange={(event) => handleShowAccountCodeIdCheckbox(event.target.checked)}
                                    name="ShowAccountCodeId"
                                    color="primary"
                                    sx={{ padding: '2px' }}
                                    size="small"
                                />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                                    {t('ShowAccountCodeId')}
                                </span>
                            </div>

                            {detailedTransactions && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    backgroundColor: '#f8f9ff',
                                    borderRadius: '8px',
                                    border: '1px solid #e3e8ff',
                                    height: '36px',
                                }}>
                                    <Checkbox
                                        checked={showOpeningColumns}
                                        onChange={(event) => handleShowOpeningColumnsCheckbox(event.target.checked)}
                                        name="ShowOpeningColumns"
                                        color="primary"
                                        sx={{ padding: '2px' }}
                                        size="small"
                                    />
                                    <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                                        {t('ShowOpeningBalanceColumns')}
                                    </span>
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: '#f8f9ff',
                                borderRadius: '8px',
                                border: '1px solid #e3e8ff',
                                height: '36px',
                            }}>
                                <Checkbox
                                    checked={hideZeroRows}
                                    onChange={(event) => handleHideZeroRowsCheckbox(event.target.checked)}
                                    name="HideZeroRows"
                                    color="primary"
                                    sx={{ padding: '2px' }}
                                    size="small"
                                />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                                    {t('HideZeroRows')}
                                </span>
                            </div>

                            <StyledToggleButtonGroup
                                value={trialBalanceType}
                                exclusive
                                onChange={handleTrialBalanceTypeChange}
                                aria-label="Platform"
                            >
                                <ToggleButton value="PalCodes" startIcon={<AccountBalanceIcon />} sx={{ fontSize: '18px' }}>
                                    {t("ByBalanceClassification")}
                                </ToggleButton>
                                <ToggleButton value="Categories" startIcon={<CategoryIcon />}>
                                    {t("ByCategory")}
                                </ToggleButton>
                                <ToggleButton value="AccountCards" startIcon={<TableChartIcon />}>
                                    {t("ByAccountCard")}
                                </ToggleButton>
                                {client?.managesProject && (
                                <ToggleButton value="Projects" startIcon={<WorkOutlineIcon />}>
                                    {t("ByProjects")}
                                </ToggleButton>
                                )}
                            </StyledToggleButtonGroup>
                            {client?.managesProject && trialBalanceType === 'Projects' && (
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginTop: '6px',
                                        paddingBottom: '4px',
                                    }}
                                >
                                    {loadingSegmentations && <CircularProgress size={22} />}
                                    <Autocomplete
                                        size="small"
                                        loading={loadingSegmentations}
                                        options={segmentationProjects.map((p) => ({
                                            id: String(p.id),
                                            code: p.code,
                                            name: p.name,
                                            label: p.label,
                                        }))}
                                        getOptionLabel={(opt) => opt?.label ?? ''}
                                        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                        value={selectedFromSegmentation}
                                        onChange={handleFromSegmentationChange}
                                        PopperComponent={StyledPopper}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('FromProject')}
                                                size="small"
                                                dir={direction}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        background: '#F5F5F6',
                                                        '&:focus-within': {
                                                            outline: '2px solid #304FFF',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        clearOnBlur={false}
                                        dir={direction}
                                        sx={{ minWidth: 260, maxWidth: 360 }}
                                    />
                                    <Autocomplete
                                        size="small"
                                        loading={loadingSegmentations}
                                        options={segmentationProjects.map((p) => ({
                                            id: String(p.id),
                                            code: p.code,
                                            name: p.name,
                                            label: p.label,
                                        }))}
                                        getOptionLabel={(opt) => opt?.label ?? ''}
                                        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                        value={selectedToSegmentation}
                                        onChange={handleToSegmentationChange}
                                        PopperComponent={StyledPopper}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('ToProject')}
                                                size="small"
                                                dir={direction}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        background: '#F5F5F6',
                                                        '&:focus-within': {
                                                            outline: '2px solid #304FFF',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        clearOnBlur={false}
                                        dir={direction}
                                        sx={{ minWidth: 260, maxWidth: 360 }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div dir={previewDir} dangerouslySetInnerHTML={{ __html: displayHtml }} />
                </Box>


            </Modal>
            {/* Monthly Report Modal */}
            <TrialBalanceMonthlyReportModal
                open={showMonthlyReportModal}
                onClose={handleCloseMonthlyReportModal}
                direction={direction}
                t={t}
                client={client}
                token={token}
                palCodesData={palCodesData}
                palDates={palDates}
                isMonthly={isMonthly}
                userTypeContext={userTypeContext}
            />

            {/* Period Selection Modal */}
            <PeriodSelectionModal
                open={showPeriodSelectionModal}
                onClose={handleClosePeriodSelection}
                selectedPeriod1={selectedPeriod1}
                selectedPeriod2={selectedPeriod2}
                setSelectedPeriod1={setSelectedPeriod1}
                setSelectedPeriod2={setSelectedPeriod2}
                onFetchComparisonData={handleFetchComparisonDataWithRebuild}
                isLoadingComparison={isLoadingComparison}
                direction={direction}
                t={t}
            />

            {/* Signature Modal */}
            <SignatureModal
                direction={direction}
                open={showSignatureModal}
                handleClose={() => setShowSignatureModal(false)}
                onSendWithSignature={handleSendWithSignature}
                reportPreview={`${data?.clientName} - ${t('TrialBalanceReport') || 'Trial Balance Report'}`}
            />
        </>
    );
};

const groupBy = (arr, key) => {
    return arr.reduce((acc, item) => {
        if (!acc[item[key]]) {
            acc[item[key]] = [];
        }
        acc[item[key]].push(item);
        return acc;
    }, {});
};

export default TrialBalanceReportHtml;
